<template>
  <div :class="embedded ? 'embedded-map-container' : 'proxima-wrap'">

    <!-- EMBEDDED MODE -->
    <template v-if="embedded">
      <div class="embedded-iframe-wrapper">
        <div v-if="embeddedSelectedElementLabel" class="embedded-filter-pill">
          {{ embeddedSelectedElementLabel }}
        </div>
        <div v-if="inProgress" class="embedded-loading-overlay">
          <div class="custom-loader">
            <div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/>
          </div>
          <p class="loading-text">Loading map…</p>
        </div>
        <iframe v-else id="interactiveMapProxima-embedded"
                :src="`${envs('MAP_BASE')}${getMapStyle()}?version=242&embedded=true`"
                frameborder="0" style="width:100%;height:100%;display:block;" />
      </div>
    </template>

    <!-- FULL MODE -->
    <template v-else>
      <div class="py-4 pb-0 container-fluid">
        <div class="section-header-card card">
          <div class="card-body p-0">
            <div class="row gx-4 align-items-center justify-content-between">
              <div class="col-auto my-auto">
                <h5 class="mb-0 header-title" @click="tableView = 'PROFILES'">
                  Asset Profiles
                  <span v-if="PUBLISHED_PROFILES?.assetProfiles?.length || assetsPlotted > 0">
                    ({{ assetsPlotted != -1 ? assetsPlotted : PUBLISHED_PROFILES?.assetProfiles?.length ?? 0 }})
                  </span>
                </h5>
                <p class="mb-0 text-xs text-muted">Please use the below filters and buttons on the side to navigate</p>
              </div>
              <div class="col-auto my-auto">
                <button class="filter-btn filter-btn--primary notes-header-btn" @click="showNotesDrawer = true">
                  <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Notes.png"
                       class="section-icon" alt="Notes" />
                  Notes
                  <span v-if="notes.length > 0" class="notes-badge">{{ notes.length }}</span>
                </button>
              </div>
            </div>
            <div class="page-actions">
              <button class="page-btn" :disabled="inProgress" @click="showMap = !showMap">
                {{ showMap ? "View as table" : "Display on Map" }}
              </button>
              <div style="width:0.5px;height:22px;background:rgba(0,0,0,0.12);margin:0.5rem 0.25rem;flex-shrink:0;" />
              <NDropdown
                  v-if="showMap"
                  trigger="click"
                  placement="bottom-start"
                  :options="MAP_STYLE_OPTS"
                  @select="updateMapStyle"
                  :theme-overrides="{
                    color: 'rgba(255, 255, 255, 0.35)',
                    optionTextColor: '#000',
                    optionTextColorHover: '#2184ff',
                    fontSize: '0.75rem',
                    optionPadding: '0.5rem 0.5rem',
                    borderRadius: '0px',
                    boxShadow: '0 4px 28px rgba(0, 0, 0, 0.12)',
                  }"
                  style="backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);"
              >
                <button class="page-btn" :disabled="inProgress">Map Style</button>
              </NDropdown>
              <div v-if="showMap" style="width:0.5px;height:22px;background:rgba(0,0,0,0.12);margin:0.5rem 0.25rem;flex-shrink:0;" />
              <NDropdown
                  v-if="showMap"
                  trigger="click"
                  placement="bottom-start"
                  :options="MAP_OPTS"
                  @select="updateMapType"
                  :theme-overrides="{
                    color: 'rgba(255, 255, 255, 0.35)',
                    optionTextColor: '#000',
                    optionTextColorHover: '#2184ff',
                    fontSize: '0.75rem',
                    optionPadding: '0.5rem 0.5rem',
                    borderRadius: '0px',
                    boxShadow: '0 4px 28px rgba(0, 0, 0, 0.12)',
                  }"
                  style="backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);"
              >
                <button class="page-btn" :disabled="inProgress">Map projection</button>
              </NDropdown>
              <div v-if="showMap" style="width:0.5px;height:22px;background:rgba(0,0,0,0.12);margin:0.5rem 0.25rem;flex-shrink:0;" />
              <button class="page-btn" :disabled="inProgress" @click="saveToPNG">
                {{ showMap ? "Export Graphic" : "Download Data Table" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="tableView === 'PROFILES'" class="container-fluid profiles-layout">
        <!-- Sidebar -->
        <div id="sidebar" class="sidebar" :class="sidebarCollapsed ? 'sidebar--collapsed' : ''">
          <button id="btnFilter" @click="toggleMenu" class="collapse-btn">
            <span id="chevron" :class="sidebarCollapsed ? 'chevron-left' : 'chevron-right'"></span>
          </button>

          <div v-show="!sidebarCollapsed" class="sidebar-inner">
            <div class="tab-toggle" @click="openTab(activeTab === 1 ? 2 : 1)">
              <span class="tab-toggle__label">{{ activeTab === 1 ? 'Filters' : 'Map Layout' }}</span>
              <div class="tab-toggle__thumb" :class="activeTab === 2 ? 'tab-toggle__thumb--left' : ''"></div>
            </div>

            <!-- Filters tab -->
            <div v-show="activeTab===1" class="filter-panel">
              <div class="filter-inputs modern-scrollbar">
                <NSelect v-model:value="LAST_SELECTED_ELEMENT_TO_VIEW"
                         :theme-overrides="inputTheme"
                         :options="sortBy(SUBSCRIPTION_ELEMENTS_OPTS, 'label')" multiple filterable clearable
                         :placeholder="t('ProximaList.profilesTable.elementsPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="COUNTRY_TO_VIEW" :options="ASSET_COUNTRIES_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.countriesPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="COMPANY_TO_VIEW" :options="ASSET_COMPANIES_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.companiesPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="ASSET_TO_VIEW" :options="ASSET_ASSETS_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.assetsPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="STATUS_TO_VIEW" :options="ASSET_STAGES_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.statusPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="TYPE_TO_VIEW" :options="ASSET_TYPES_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.typesPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="PRODUCT_GROUP_TO_VIEW" :options="ASSET_PRODUCT_GROUP_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.productGroupPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
                <NSelect v-model:value="PRODUCT_TO_VIEW" :options="ASSET_PRODUCT_OPTS" filterable clearable multiple
                         :theme-overrides="inputTheme"
                         :placeholder="t('ProximaList.profilesTable.productsPlaceholder')"
                         :disabled="inProgress" size="small" :input-props="{ autocomplete: Math.random() }" />
              </div>
              <div class="filter-btns">
                <button class="filter-btn filter-btn--primary" :disabled="inProgress" @click="getProfiles">Redraw</button>
                <button class="filter-btn filter-btn--secondary" :disabled="inProgress" @click="resetFilters">{{ $t("ProximaList.profilesTable.clear") }}</button>
              </div>
            </div>

            <!-- Layout tab -->
            <div v-show="activeTab===2" class="layout-panel modern-scrollbar">
              <p class="layout-label">Status:</p>
              <label class="custom-checkbox"><input type="checkbox" name="status" v-model="isProject" value="project"><span class="checkmark project"></span>Project</label>
              <label class="custom-checkbox"><input type="checkbox" name="status" v-model="isOperating" value="operating"><span class="checkmark operating"></span>Operating</label>
              <label class="custom-checkbox"><input type="checkbox" name="status" v-model="isClosed" value="closed"><span class="checkmark closed"></span>Closed/C&M</label>

              <p class="layout-label" style="margin-top:8px">Type:</p>
              <label class="custom-checkbox"><input type="checkbox" name="status" v-model="isMine" value="mine"><span class="checkmark-mine"></span>Mine &amp; Beneficiation</label>
              <label class="custom-checkbox"><input type="checkbox" name="status" v-model="isRefinery" value="refinery"><span class="checkmark-refinery"></span>Refinery &amp; Factory</label>
              <div v-if="hasInstallations">
                <label class="custom-checkbox" :style="{ cursor: hasInstallations ? 'pointer' : 'not-allowed', color: hasInstallations ? 'inherit' : 'lightgrey' }">
                  <input :disabled="!hasInstallations" type="checkbox" name="status" v-model="isInstallation" value="installation">
                  <span class="checkmark-installation"></span>VFB
                </label>
              </div>

              <p class="layout-label" style="margin-top:8px">Asset Group Colours:</p>
              <div class="color-row" v-for="element in LAST_SELECTED_ELEMENT_TO_VIEW" :key="element">
                <span class="color-label">{{ SUBSCRIPTION_ELEMENTS_OPTS.find(x => x.value == element)?.label ?? element }}</span>
                <input class="color-input" type="color" :value="getColor(element)" @input="updateColor(element, $event.target.value)" />
              </div>

              <div class="filter-btns" style="margin-top:8px">
                <button class="filter-btn filter-btn--primary" :disabled="inProgress" @click="checkStatus">Redraw</button>
                <button class="filter-btn filter-btn--secondary" :disabled="inProgress" @click="resetForm">Reset</button>
              </div>

              <!-- Legend -->
              <div class="legend-box">
                <p class="layout-label">Legend:</p>
                <div v-if="isProject" class="legend-row"><div class="checkmark-box gold"></div><span>Project</span></div>
                <div v-if="isOperating" class="legend-row"><div class="checkmark-box blue"></div><span>Operating</span></div>
                <div v-if="isClosed" class="legend-row"><div class="checkmark-box red"></div><span>Closed/C&M</span></div>
                <div v-if="isMine" class="legend-row"><div class="square"></div><span>Mine &amp; Beneficiation</span></div>
                <div v-if="isRefinery" class="legend-row"><div class="triangle"></div><span>Refinery &amp; Factory</span></div>
                <div v-if="isInstallation" class="legend-row"><div class="legend-circle"></div><span>VFB</span></div>
                <div v-if="!isOperating && !isProject && !isClosed">
                  <div class="legend-row" v-for="element in LAST_SELECTED_ELEMENT_TO_VIEW" :key="element">
                    <div class="legend-color-swatch" :style="{ backgroundColor: getColor(element) ?? 'white' }"></div>
                    <span>{{ SUBSCRIPTION_ELEMENTS_OPTS.find(x => x.value == element)?.label ?? element }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="main-content">
          <!-- Map -->
          <div v-show="showMap" class="map-wrapper" id="drawer-target">
            <div v-if="inProgress" class="loading-overlay">
              <div class="custom-loader">
                <div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/>
              </div>
              <p class="loading-text">Loading...</p>
            </div>
            <iframe id="interactiveMapProxima-fullview"
                    :src="`${envs('MAP_BASE')}${getMapStyle()}?version=242&embedded=false`"
                    frameborder="0"
                    style="width:100%;height:100%;display:block;border:none;" />
          </div>

          <!-- Table -->
          <div v-show="!showMap" class="table-wrap">
            <NDataTable
                id="info-table"
                :columns="columns"
                :data="inProgress ? [] : sortedData"
                :flex-height="true"
                @update:sorter="handleSort"
                :scroll-x="scrollX"
                :theme-overrides="tableTheme"
            >
              <template #empty>
                <div v-if="inProgress" class="loading-overlay loading-overlay--table">
                  <div class="custom-loader">
                    <div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/>
                  </div>
                  <p class="loading-text">Loading...</p>
                </div>
                <p v-else>No data available</p>
              </template>
            </NDataTable>
          </div>
        </div>
      </div>

      <!-- Drawers & Modal -->
      <NDrawer v-model:show="showSupplyChainAgreementInfo" :width="400" placement="right">
        <NDrawerContent>
          <h6 class="text-capitalize">{{ mountedSupplyChainAgreement?.full?.title }}</h6>
          <br />
          <div v-if="mountedSupplyChainAgreement?.full?.type">Type:&nbsp;{{ mountedSupplyChainAgreement?.full?.type }}</div>
          <div v-if="mountedSupplyChainAgreement?.full?.product">Product:&nbsp;{{ mountedSupplyChainAgreement?.full?.product }}</div>
          <div v-if="capacity">Volumes:&nbsp;{{ capacity }}</div>
          <div v-if="mountedSupplyChainAgreement?.full?.announcedDate">Date Announced:&nbsp;{{ mountedSupplyChainAgreement?.full?.announcedDate }}</div>
          <div v-if="mountedSupplyChainAgreement?.full?.announcedStartDate">Start Date:&nbsp;{{ mountedSupplyChainAgreement?.full?.announcedStartDate }}</div>
          <div v-if="mountedSupplyChainAgreement?.full?.endDate">End Date:&nbsp;{{ mountedSupplyChainAgreement?.full?.endDate }}</div>
          <br v-if="suppliers.length" /><h6 v-if="suppliers.length">Suppliers</h6>
          <div style="display:block;width:100%" v-for="nth in suppliers" :key="nth">
            <div>Company:&nbsp;{{ nth?.companyName }}</div>
            <div>Asset:&nbsp;<span style="cursor:pointer" @click="goToAssetProfile(nth)">{{ nth?.asset }}</span></div>
          </div>
          <br v-if="receivers.length" /><h6 v-if="receivers.length">Receivers</h6>
          <div style="display:block;width:100%" v-for="nth in receivers" :key="nth">
            <div>Company:&nbsp;{{ nth?.companyName }}</div>
            <div>Asset:&nbsp;<span style="cursor:pointer" @click="goToAssetProfile(nth)">{{ nth?.asset }}</span></div>
          </div>
          <br /><h6>Deal Overview</h6>
          <p>{{ mountedSupplyChainAgreement?.full?.dealProfile }}</p>
        </NDrawerContent>
      </NDrawer>

      <NDrawer v-model:show="showFilters" :width="400" :height="200" placement="right">
        <NDrawerContent></NDrawerContent>
      </NDrawer>

      <!-- ── All Notes Drawer ── -->
      <NDrawer v-model:show="showNotesDrawer" :width="380" placement="right" :trap-focus="false" :style="{ top: '70px', height: 'calc(100vh - 70px)' }">
        <NDrawerContent :native-scrollbar="false" :body-content-style="{ padding: 0 }">
          <div class="marker-drawer">
            <div class="drawer-content modern-scrollbar">
              <div class="accordion-section">
                <div class="accordion-header" style="cursor:default;">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Notes.png"
                         class="section-icon" alt="Notes" />
                    <span>Notes</span>
                    <span v-if="notes?.length > 0" class="notes-badge">
                      {{ notes.length }}
                    </span>
                  </div>
                </div>
                <div class="accordion-content">
                  <div v-if="notes?.length" class="notes-timeline-wrapper">
                    <NTimeline>
                      <NTimelineItem
                          v-for="note in notes"
                          :key="note.id"
                          type="info"
                          :title="note.title"
                          line-type="dashed"
                          style="cursor: pointer"
                          @click="handleAllNotesClick(note)"
                      >
                        <div class="paragraph note-preview">
                          {{ truncateNoteText(note.notes, 80) }}
                        </div>
                        <template #footer>
                          <NButton
                              text
                              size="small"
                              color="#767c82"
                              @click.stop="handleDeleteAllNotesEntry(note.id)"
                          >
                            {{ $t("NotesWindow.deleteButton") }}
                          </NButton>
                        </template>
                      </NTimelineItem>
                    </NTimeline>
                  </div>
                  <div v-else class="empty-state">
                    <p>No notes yet.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </NDrawerContent>
      </NDrawer>

      <div v-if="productNamesModal.show" class="pn-modal-backdrop" @click.self="productNamesModal.show = false">
        <div class="pn-modal">
          <div class="pn-modal-header">
            <span>Products</span>
            <button @click="productNamesModal.show = false" class="pn-modal-close">✕</button>
          </div>
          <div class="pn-modal-body">{{ productNamesModal.content }}</div>
        </div>
      </div>

      <NModal v-model:show="showConfirmationModal" :mask-closable="false">
        <NCard
            style="width: 480px; max-width: 95vw;"
            title="Conflicting Filters"
            :bordered="false"
        >
          <p style="font-size: 13px; color: #606266; margin-bottom: 16px;">
            You have applied an asset status filter as well as asset group colours. Only one of these filters may be applied at a time.
          </p>
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <NButton
                :theme-overrides="{
          colorHover: 'linear-gradient(90deg, #213ce5, #2380f2)',
          colorPressed: '#213ce5',
          colorFocus: '#213ce5',
          borderHover: '1px solid #00aeef',
          borderPressed: '1px solid #00aeef',
          borderFocus: '1px solid #00aeef',
          textColorHover: '#fff',
          textColorPressed: '#fff',
          textColorFocus: '#fff',
        }"
                color="#213ce5"
                style="justify-content: flex-start; background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent;"
                @click="submitForm(true)"
            >
              Retain Asset Group Colours
            </NButton>
            <NButton
                :theme-overrides="{
          colorHover: 'linear-gradient(90deg, #213ce5, #2380f2)',
          colorPressed: '#213ce5',
          colorFocus: '#213ce5',
          borderHover: '1px solid #00aeef',
          borderPressed: '1px solid #00aeef',
          borderFocus: '1px solid #00aeef',
          textColorHover: '#fff',
          textColorPressed: '#fff',
          textColorFocus: '#fff',
        }"
                color="#213ce5"
                style="justify-content: flex-start; background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent;"
                @click="submitForm(false)"
            >
              Apply Status Filter
            </NButton>
          </div>
          <template #footer>
            <NButton
                :theme-overrides="{
          borderHover: '1px solid #00aeef',
          borderPressed: '1px solid #00aeef',
          borderFocus: '1px solid #00aeef',
          textColorHover: '#213ce5',
          textColorPressed: '#213ce5',
          textColorFocus: '#213ce5',
        }"
                style="border: 1px solid #213ce5; color: #213ce5;"
                @click="showConfirmationModal = false"
            >
              Cancel
            </NButton>
          </template>
        </NCard>
      </NModal>
    </template>

    <!-- ── Marker Info Drawer ── -->
    <NDrawer v-model:show="showMarkerInfo" :width="380" placement="right" :trap-focus="false" :style="{ top: '70px', height: 'calc(100vh - 70px)' }">
      <NDrawerContent :native-scrollbar="false" :body-content-style="{ padding: 0 }">

        <!-- ── ASSET PROFILE ── -->
        <template v-if="PROFILE_TO_VIEW_IN_DRAWER?.type === 'ASSET_PROFILE'">
          <div class="marker-drawer">
            <div class="drawer-content modern-scrollbar">

              <!-- Asset Name + Stage header -->
              <div class="marker-drawer-header">
                <div class="marker-drawer-header__row">
                  <span class="marker-drawer-header__name">{{ PROFILE_TO_VIEW_IN_DRAWER.title }}</span>
                  <span
                      class="marker-drawer-header__badge"
                      :style="{ background: formatStatusColor(PROFILE_TO_VIEW_IN_DRAWER.status) }"
                  >{{ PROFILE_TO_VIEW_IN_DRAWER.status || 'Unknown' }}</span>
                </div>
              </div>

              <!-- Location Accuracy -->
              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.coordinates" class="accordion-section">
                <button @click="toggleMarkerSection('location')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/World.png"
                         class="section-icon" alt="Location" />
                    <span style="font-weight: 500; font-family: 'GilroyMedium' !important;">
                      <b>Location Accuracy:</b>
                      {{ PROFILE_TO_VIEW_IN_DRAWER.accuracy?.toLowerCase().replace(/^\w/, c => c.toUpperCase()) }}
                    </span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.location }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.location" class="accordion-content">
                    <p class="marker-drawer-coords">
                      <i class="fas fa-map-pin marker-drawer-coords__pin"></i>
                      {{ PROFILE_TO_VIEW_IN_DRAWER.coordinates }}
                    </p>
                  </div>
                </Transition>
              </div>

              <!-- Ownership -->
              <div v-if="normalizedOwners.length" class="accordion-section">
                <button @click="toggleMarkerSection('ownership')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Ownership.png"
                         class="section-icon" alt="Ownership" />
                    <span>Ownership</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.ownership }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.ownership" class="accordion-content">
                    <div class="marker-drawer-ownership">
                      <div class="marker-drawer-ownership__list">
                        <div v-for="owner in normalizedOwners" :key="owner.id" class="marker-drawer-owner">
                          <span class="marker-drawer-owner__pct" v-if="owner.percentage != null">{{ owner.percentage }}%</span>
                          <span class="marker-drawer-owner__pct" v-else style="visibility:hidden;">0%</span>
                          <span class="marker-drawer-owner__name">{{ owner.name }}</span>
                        </div>
                      </div>
                      <svg
                          v-if="PROFILE_TO_VIEW_IN_DRAWER.owners.length >= 1"
                          width="54" height="54" viewBox="0 0 54 54"
                          class="marker-drawer-ownership__pie"
                      >
                        <g transform="translate(27,27)">
                          <path
                              v-for="(slice, i) in ownershipPieSlices"
                              :key="i"
                              :d="slice.d"
                              :fill="slice.fill"
                          />
                        </g>
                      </svg>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- Element Tiles -->
              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.primary_Commodities?.length" class="accordion-section">
                <button @click="toggleMarkerSection('elements')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Commodity%20Overview%26Subscriptions.png"
                         class="section-icon" alt="Elements" />
                    <span>Elements</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.elements }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.elements" class="accordion-content">
                    <div class="marker-drawer-elements">
                      <div v-for="(url, i) in (PROFILE_TO_VIEW_IN_DRAWER.elementIconUrls ?? [])" :key="i" class="marker-drawer-el-tile">
                        <img :src="url" :alt="PROFILE_TO_VIEW_IN_DRAWER.primary_Commodities[i]"
                             style="width:38px;height:38px;object-fit:contain;" />
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- Products -->
              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.products?.length" class="accordion-section">
                <button @click="toggleMarkerSection('production')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Factory.png"
                         class="section-icon" alt="Products" />
                    <span>Products</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.production }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.production" class="accordion-content">
                    <div
                        v-for="(product, i) in sortBy(PROFILE_TO_VIEW_IN_DRAWER.products, 'productName')"
                        :key="i"
                        style="margin-bottom:8px;"
                    >
                      <p style="font-size:12px;font-weight:600;color:#000;margin:0 0 2px;">
                        {{ product.productName || '—' }}
                        <span v-if="product.status" style="font-size:10px;font-weight:400;color:#718096;">({{ product.status }})</span>
                      </p>
                      <p class="marker-drawer-body-text" style="color:#444;">
                        Capacity: {{ formatProductCapacity(product) || '—' }}
                        <template v-if="product.commission"><br />Commissioned: {{ product.commission }}</template>
                      </p>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- Profile -->
              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.profile" class="accordion-section">
                <button @click="toggleMarkerSection('profile')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Profile.png"
                         class="section-icon" alt="Profile" />
                    <span>Profile</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.profile }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.profile" class="accordion-content">
                    <p class="marker-drawer-body-text">{{ PROFILE_TO_VIEW_IN_DRAWER.profile }}</p>
                  </div>
                </Transition>
              </div>

              <!-- Notes -->
              <div class="accordion-section">
                <button @click="toggleMarkerSection('notes')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Notes.png"
                         class="section-icon" alt="Notes" />
                    <span>Notes</span>
                    <span v-if="drawerProfileNotes.length > 0" class="notes-badge">
                      {{ drawerProfileNotes.length }}
                    </span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.notes }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.notes" class="accordion-content accordion-content--notes">

                    <!-- Existing notes list -->
                    <div v-if="drawerProfileNotes.length" class="notes-timeline-wrapper">
                      <NTimeline>
                        <NTimelineItem
                            v-for="note in drawerProfileNotes"
                            :key="note.id"
                            type="info"
                            :title="note.title"
                        >
                          <div class="note-preview">{{ note.notes || note.note }}</div>
                          <template #footer>
                            <span class="note-date">{{ formatNoteDate(note.createdAt) }}</span>
                            <NButton text size="small" color="#767c82" @click="handleDeleteDrawerNote(note.id)">
                              Delete
                            </NButton>
                          </template>
                        </NTimelineItem>
                      </NTimeline>
                    </div>
                    <div v-else class="empty-state">
                      <p>No notes yet.</p>
                    </div>

                    <!-- Inline add note form toggle -->
                    <div class="add-note-toggle" @click="showAddNoteForm = !showAddNoteForm">
                      <div class="add-note-toggle__line"></div>
                      <span class="add-note-toggle__label">
                        Add note
                        <i class="fas" :class="showAddNoteForm ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </span>
                      <div class="add-note-toggle__line"></div>
                    </div>

                    <!-- Inline add note form -->
                    <div v-if="showAddNoteForm" class="inline-note-form">
                      <NInput
                          v-model:value="inlineNote.title"
                          type="text"
                          placeholder="Note title"
                          size="small"
                          :theme-overrides="noteInputTheme"
                          style="margin-bottom:6px;"
                      />
                      <NInput
                          v-model:value="inlineNote.notes"
                          type="textarea"
                          placeholder="Write your note…"
                          size="small"
                          :rows="3"
                          :theme-overrides="noteInputTheme"
                          style="margin-bottom:8px;"
                      />
                      <div style="display:flex;gap:6px;justify-content:flex-end;">
                        <NButton size="small" ghost color="#767c82" @click="clearInlineNote" :theme-overrides="cancelBtnTheme">
                          Clear
                        </NButton>
                        <NButton
                            size="small"
                            style="background:#213ce5;color:#fff;border:none;"
                            :loading="noteSaving"
                            :disabled="noteSaving"
                            @click="saveInlineNote"
                        >
                          Save note
                        </NButton>
                      </div>
                    </div>

                  </div>
                </Transition>
              </div>

              <!-- News -->
              <div class="accordion-section">
                <button @click="toggleMarkerSection('news')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/News.png"
                         class="section-icon" alt="News" />
                    <span>News</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.news }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.news" class="accordion-content" style="padding: 0;">
                    <!-- Loading -->
                    <div v-if="DRAWER_ARTICLES.loading && !DRAWER_ARTICLES.data?.length"
                         style="display:flex;align-items:center;gap:8px;padding:0.5rem 1.25rem;">
                      <div class="custom-loader" style="transform:scale(0.7);transform-origin:left;">
                        <div class="loader-circle"/><div class="loader-circle"/><div class="loader-circle"/>
                      </div>
                      <span style="font-size:12px;color:#718096;">Loading…</span>
                    </div>
                    <!-- Articles list -->
                    <div v-else-if="DRAWER_ARTICLES.data?.length" class="drawer-news-list">
                      <div
                          v-for="item in DRAWER_ARTICLES.data"
                          :key="item.id"
                          class="drawer-news-item"
                          @click="viewArticle(item)"
                      >
                        <div class="news-meta">
                          <span class="news-date">{{ formatDate(item.publish_Date) }}</span>
                          <span v-if="item.authors?.length" class="meta-separator">|</span>
                          <span v-if="item.authors?.length" class="news-authors">
                            <span v-for="author in item.authors" :key="author.name" class="author">
                              <a :href="getAuthorUrl(author.name)" class="author-link" target="_blank" @click.stop>
                                {{ author.name }}
                              </a>
                            </span>
                          </span>
                        </div>
                        <h6 class="drawer-news-title">{{ item.name }}</h6>
                        <div class="drawer-news-description" v-html="item.description" />
                      </div>

                      <!-- Load more -->
                      <div v-if="DRAWER_ARTICLES.hasMore" style="padding:8px 1.25rem;text-align:center;">
                        <button
                            @click.stop="() => getDrawerArticles(PROFILE_TO_VIEW_IN_DRAWER, DRAWER_ARTICLES.page + 1)"
                            :disabled="DRAWER_ARTICLES.loading"
                            style="font-size:11px;color:#213ce5;background:none;border:none;cursor:pointer;padding:0;font-weight:600;"
                            :style="DRAWER_ARTICLES.loading ? 'opacity:0.5;cursor:not-allowed;' : ''"
                        >
                          {{ DRAWER_ARTICLES.loading ? 'Loading…' : `Load more (${DRAWER_ARTICLES.count - DRAWER_ARTICLES.data.length} remaining)` }}
                        </button>
                      </div>
                    </div>
                    <div v-else class="empty-state">
                      <p>No news available.</p>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- ── Project ── -->
              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.project" class="accordion-section">
                <button @click="toggleMarkerSection('project')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Project.png"
                         class="section-icon" alt="Project" />
                    <span>Project</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.project }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.project" class="accordion-content">
                    <p class="marker-drawer-body-text">{{ PROFILE_TO_VIEW_IN_DRAWER.project }}</p>
                  </div>
                </Transition>
              </div>

            </div>
          </div>
        </template>

        <!-- ── COMPANY PROFILE ── -->
        <template v-else-if="PROFILE_TO_VIEW_IN_DRAWER?.type === 'COMPANY_PROFILE'">
          <div class="marker-drawer">
            <div class="drawer-content modern-scrollbar">

              <div class="marker-drawer-header">
                <div class="marker-drawer-header__row">
                  <span class="marker-drawer-header__name">{{ PROFILE_TO_VIEW_IN_DRAWER.title }}</span>
                  <span
                      v-if="PROFILE_TO_VIEW_IN_DRAWER.ownershipType"
                      class="marker-drawer-header__badge"
                      style="background: #00213b;"
                  >{{ PROFILE_TO_VIEW_IN_DRAWER.ownershipType }}</span>
                </div>
                <a
                  v-if="PROFILE_TO_VIEW_IN_DRAWER.website"
                  :href="PROFILE_TO_VIEW_IN_DRAWER.website"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="font-size:11px;color:#213ce5;text-decoration:none;display:inline-block;margin-top:4px;"
                  @click.stop
                  >
                  {{ PROFILE_TO_VIEW_IN_DRAWER.website.replace(/^https?:\/\//, '') }}
                  <i class="fas fa-external-link-alt" style="font-size:9px;margin-left:2px;"></i>
                </a>
              </div>

              <!-- Headquarters -->
              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.headquarterCountry" class="accordion-section">
                <button @click="toggleMarkerSection('location')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/World.png"
                         class="section-icon" alt="Location" />
                    <span>Headquarters</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.location }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.location" class="accordion-content">
                    <p class="marker-drawer-body-text">{{ PROFILE_TO_VIEW_IN_DRAWER.headquarterCountry }}</p>
                  </div>
                </Transition>
              </div>

              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.overview" class="accordion-section">
                <button @click="toggleMarkerSection('overview')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Profile.png"
                         class="section-icon" alt="Overview" />
                    <span>Overview</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.overview }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.overview" class="accordion-content">
                    <p class="marker-drawer-body-text">{{ PROFILE_TO_VIEW_IN_DRAWER.overview }}</p>
                  </div>
                </Transition>
              </div>

              <div v-if="PROFILE_TO_VIEW_IN_DRAWER.profile" class="accordion-section">
                <button @click="toggleMarkerSection('profile')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Profile.png"
                         class="section-icon" alt="Profile" />
                    <span>Profile</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.profile }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.profile" class="accordion-content">
                    <p class="marker-drawer-body-text">{{ PROFILE_TO_VIEW_IN_DRAWER.profile }}</p>
                  </div>
                </Transition>
              </div>

              <div
                  v-if="PROFILE_TO_VIEW_IN_DRAWER.miningCommodities?.length || PROFILE_TO_VIEW_IN_DRAWER.refiningProducts?.length"
                  class="accordion-section"
              >
                <button @click="toggleMarkerSection('commodities')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Commodity%20Overview%26Subscriptions.png"
                         class="section-icon" alt="Commodities" />
                    <span>Commodities</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.commodities }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.commodities" class="accordion-content">
                    <div v-if="PROFILE_TO_VIEW_IN_DRAWER.miningCommodities?.length" style="margin-bottom:8px;">
                      <p style="font-size:11px;font-weight:600;color:#718096;margin:0 0 4px;">Mining</p>
                      <!-- Commodity overview tiles -->
                      <div v-if="PROFILE_TO_VIEW_IN_DRAWER.commodityOverview?.length">
                        <div
                            v-for="item in PROFILE_TO_VIEW_IN_DRAWER.commodityOverview"
                            :key="item.commodity"
                            style="margin-bottom:8px;"
                        >
                          <p style="font-size:12px;font-weight:600;color:#000;margin:0 0 2px;">{{ item.commodity }}</p>
                          <p class="marker-drawer-body-text" style="color:#444;">{{ item.overview }}</p>
                        </div>
                      </div>
                      <!-- Fallback: just the codes -->
                      <p v-else class="marker-drawer-body-text">
                        {{ PROFILE_TO_VIEW_IN_DRAWER.miningCommodities.join(', ') }}
                      </p>
                    </div>
                    <div v-if="PROFILE_TO_VIEW_IN_DRAWER.refiningProducts?.length">
                      <p style="font-size:11px;font-weight:600;color:#718096;margin:0 0 4px;">Refining</p>
                      <p class="marker-drawer-body-text">{{ PROFILE_TO_VIEW_IN_DRAWER.refiningProducts.join(', ') }}</p>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- Stock Exchanges -->
              <div
                  v-if="PROFILE_TO_VIEW_IN_DRAWER.stockExchanges?.filter(x => x.stockExchange?.length).length"
                  class="accordion-section"
              >
                <button @click="toggleMarkerSection('exchanges')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Ownership.png"
                         class="section-icon" alt="Stock Exchanges" />
                    <span>Stock Exchanges</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.exchanges }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.exchanges" class="accordion-content">
                    <div
                        v-for="nth in PROFILE_TO_VIEW_IN_DRAWER.stockExchanges.filter(x => x.stockExchange?.length)"
                        :key="nth.stockExchange"
                        style="margin-bottom:4px;"
                    >
                      <p class="marker-drawer-body-text">
                        <span style="font-weight:600;">{{ nth.stockExchange }}</span>
                        <span v-if="nth.stocks?.flat().length">: {{ nth.stocks.flat().join(', ') }}</span>
                      </p>
                    </div>
                  </div>
                </Transition>
              </div>

              <!-- Notes -->
              <div class="accordion-section">
                <button @click="toggleMarkerSection('notes')" class="accordion-header">
                  <div class="header-left">
                    <img src="https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/Notes.png"
                         class="section-icon" alt="Notes" />
                    <span>Notes</span>
                    <span v-if="drawerProfileNotes.length > 0" class="notes-badge">
              {{ drawerProfileNotes.length }}
            </span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotated': markerDrawerSections.notes }"></i>
                </button>
                <Transition name="expand">
                  <div v-if="markerDrawerSections.notes" class="accordion-content accordion-content--notes">
                    <div v-if="drawerProfileNotes.length" class="notes-timeline-wrapper">
                      <NTimeline>
                        <NTimelineItem
                            v-for="note in drawerProfileNotes"
                            :key="note.id"
                            type="info"
                            :title="note.title"
                        >
                          <div class="note-preview">{{ note.notes || note.note }}</div>
                          <template #footer>
                            <span class="note-date">{{ formatNoteDate(note.createdAt) }}</span>
                            <NButton text size="small" color="#767c82" @click="handleDeleteDrawerNote(note.id)">
                              Delete
                            </NButton>
                          </template>
                        </NTimelineItem>
                      </NTimeline>
                    </div>
                    <div v-else class="empty-state"><p>No notes yet.</p></div>

                    <div class="add-note-toggle" @click="showAddNoteForm = !showAddNoteForm">
                      <div class="add-note-toggle__line"></div>
                      <span class="add-note-toggle__label">
                Add note
                <i class="fas" :class="showAddNoteForm ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </span>
                      <div class="add-note-toggle__line"></div>
                    </div>

                    <div v-if="showAddNoteForm" class="inline-note-form">
                      <NInput
                          v-model:value="inlineNote.title"
                          type="text"
                          placeholder="Note title"
                          size="small"
                          :theme-overrides="noteInputTheme"
                          style="margin-bottom:6px;"
                      />
                      <NInput
                          v-model:value="inlineNote.notes"
                          type="textarea"
                          placeholder="Write your note…"
                          size="small"
                          :rows="3"
                          :theme-overrides="noteInputTheme"
                          style="margin-bottom:8px;"
                      />
                      <div style="display:flex;gap:6px;justify-content:flex-end;">
                        <button
                            style="height:28px;padding:0 12px;background:transparent;color:#767c82;border:1px solid #767c82;border-radius:3px;font-size:12px;cursor:pointer;font-family:'GilroyMedium',sans-serif;"
                            @mouseenter="e => { e.target.style.color='#213ce5'; e.target.style.borderColor='#213ce5'; }"
                            @mouseleave="e => { e.target.style.color='#767c82'; e.target.style.borderColor='#767c82'; }"
                            @click="clearInlineNote"
                        >Clear</button>
                        <button
                            style="height:28px;padding:0 12px;background:#213ce5;color:#fff;border:none;border-radius:3px;font-size:12px;cursor:pointer;font-family:'GilroyMedium',sans-serif;"
                            :style="noteSaving ? 'opacity:0.6;cursor:not-allowed;' : ''"
                            :disabled="noteSaving"
                            @click="saveInlineNote"
                        >{{ noteSaving ? 'Saving…' : 'Save note' }}</button>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>

            </div>
          </div>
        </template>
      </NDrawerContent>
    </NDrawer>
  </div>
</template>

<script>
export default { name: "ProximaProfiles" };
</script>

<script setup>
import { computed, h, nextTick, onBeforeMount, onMounted, reactive, ref, watch } from "vue";
import { NDataTable, NCard, NSelect, NDrawer, NDrawerContent, NDropdown, NInput, NButton, NTimeline, NTimelineItem, NModal } from "naive-ui";
import { has_notes, USER_NOTES, getUserNotes } from "../../composables/notes/list";
import { updateOrCreateNote, mountNote, unMountNote, inProgress as noteInProgress, MOUNTED_USER_NOTE } from "../../composables/notes/note";
import { deleteNote as _deleteNote } from "../../composables/notes/note";
import pbDomain from "../../utils/pbDomain";

import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { ROUTES } from "../../routes/names";
import { logUserAction, USER_ACTIONS } from "@/composables/userLogs";
import { LAST_SELECTED_ELEMENT_TO_VIEW, LAST_SELECTED_PACKAGE_TO_VIEW, COUNTRY_TO_VIEW, COMPANY_TO_VIEW, ASSET_TO_VIEW, STATUS_TO_VIEW, TYPE_TO_VIEW, PRODUCT_GROUP_TO_VIEW, PRODUCT_TO_VIEW, DDICONS } from "../../composables/generics";
import {
  SUPPLY_DATA,
  getSupplyData,
  PUBLISHED_PROFILES,
  getPublishedProfiles,
  inProgress,
  PROFILES_FILTERS
} from "../../composables/profiles/list";
import {
  MOUNTED_ASSET_PROFILES, mountProfile as _mountAssetProfile, getAssetProfiles, PROFILE_ID,
  ASSET_STATUS_OPTS, ASSET_COUNTRIES_OPTS, ASSET_COMPANIES_OPTS, ASSET_ASSETS_OPTS,
  ASSET_STAGES_OPTS, ASSET_TYPES_OPTS, ASSET_PRODUCT_GROUP_OPTS, ASSET_PRODUCT_OPTS
} from "../../composables/profiles/asset";
import {
  mountProfile as _mountCountryProfile, getCountryProfiles, COUNTRY_COMMODITIES,
  MOUNTED_COUNTRY_PROFILES, getCountryCommodities, COUNTRY_ID
} from "../../composables/profiles/country";
import { PRODUCT_GROUP_OPTS, MARKET_SERVICES_OPTS } from "../../utils/constants";
import TableHeader from "../../components/ProfileTable/TableHeader";
import TableCell from "../../components/ProfileTable/TableCell";
import TableCellType from "../../components/ProfileTable/TableCellType";
import TableCellSignificance from "../../components/ProfileTable/TableCellSignificance";
import TableCellStatus from "../../components/ProfileTable/TableCellStatus";
import TableCellProducts from "../../components/ProfileTable/TableCellProducts";
import CompanyProfile from "../../components/Profiles/CompanyProfile.vue";
import CountryProfile from "../../components/Profiles/CountryProfile.vue";
import ProfilesMenu from "../../components/ProfilesMenu.vue";
import { envs, log } from "../../utils";
import SummaryCard from "../../components/dumb/SummaryCard.vue";
import SelectElement from "./SelectElement.vue";
import { SUBSCRIPTION_INFO } from "../../composables/subscription";
import { sortBy, uniqBy } from "lodash";
import { SUBSCRIPTION_ELEMENTS_OPTS, SUBSCRIPTION_PACKAGES_OPTS, PARENT_SUBSCRIPTION_PACKAGES_OPTS } from "../../composables/subscription";
import LatestNews from '../../components/LatestNews.vue';
import { tableData, sortTableColumn } from "../../composables/tableColumnSort.js";
import HttpClient from "project-blue-http-client";
import { AUTHENTICATION_URL } from "../../utils/constants.js";
import { getSubscriptionInfo } from "../../composables/subscription";
import * as XLSX from "xlsx";
import { userEmail } from "@/composables/userLogs";
import { getPublicArticles, PUBLIC_ARTICLES, inProgress as articlesInProgress, DRAWER_ARTICLES, getDrawerArticles } from "../../composables/profiles/articles";
const currentMapStyle = ref(localStorage.getItem("map_style") ?? "Outdoor");
const currentMapProjection = ref("equirectangular");
const showConfirmationModal = ref(false);

const showNotesDrawer = ref(false);

const notes = computed(() => {
  return USER_NOTES.value?.data || [];
});

const truncateNoteText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const handleAllNotesClick = (note) => {
  showNotesDrawer.value = false;

  if (note.profileUUID && note.profileType) {
    let routeName;
    if (note.profileType === 'ASSET') routeName = ROUTES.ASSET_PROFILE.name;
    else if (note.profileType === 'COMPANY') routeName = ROUTES.COMPANY_PROFILE.name;
    else if (note.profileType === 'COUNTRY') routeName = ROUTES.COUNTRY_PROFILE.name;

    if (routeName) {
      router.push({
        name: routeName,
        params: { id: note.profileUUID },
        query: { element: note.element }
      });
    }
  }
};

const handleDeleteAllNotesEntry = async (noteId) => {
  if (confirm('Are you sure you want to delete this note?')) {
    await _deleteNote(noteId);
    await getUserNotes();
  }
};

const inputTheme = {
  peers: {
    InternalSelection: {
      borderHover: '1px solid #213ce5',
      borderFocus: '1px solid #213ce5',
      borderActive: '1px solid #213ce5',
      boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowActive: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowHover: 'none',
      caretColor: '#213ce5',
      arrowColorActive: '#213ce5',
      arrowColorHover: '#213ce5',
      color: '#fff',
      colorActive: '#ffffff',
      colorFocus: '#ffffff',
      fontFamily: "'GilroyMedium', sans-serif",
      fontSize: '11px !important',
    },
    InternalSelectMenu: {
      optionColorActive: 'rgba(33, 60, 229, 0.08)',
      optionColorActivePending: 'rgba(33, 60, 229, 0.12)',
      optionColorPending: 'rgba(33, 60, 229, 0.05)',
      optionTextColorActive: '#213ce5',
      optionTextColorChildActive: '#213ce5',
      optionCheckColor: '#213ce5',
      actionDividerColor: '#213ce5',
      loadingColor: '#213ce5',
      scrollbarColor: 'rgba(33, 60, 229, 0.3)',
      scrollbarColorHover: 'rgba(33, 60, 229, 0.5)',
      fontFamily: "'GilroyMedium', sans-serif",
      fontSize: '11px !important',
    }
  }
};

const noteInputTheme = {
  borderHover: '1px solid #213ce5',
  borderFocus: '1px solid #213ce5',
  boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
};

const cancelBtnTheme = {
  borderHover: '1px solid #213ce5',
  borderPressed: '1px solid #213ce5',
  borderFocus: '1px solid #213ce5',
  textColorHover: '#213ce5',
  textColorPressed: '#213ce5',
  textColorFocus: '#213ce5',
};
const tableTheme = {
  thColorSorting: 'transparent',
  thIconColorActive: '#213ce5',
  thIconColor: 'rgba(33, 60, 229, 0.3)',
  sorterColor: '#213ce5',
  sorterColorActive: '#213ce5',
};
// ── Inline note state ──────────────────────────────────────────────────────
const inlineNote = reactive({ title: '', notes: '' });
const noteSaving = ref(false);
const showAddNoteForm = ref(false);

const clearInlineNote = () => {
  inlineNote.title = PROFILE_TO_VIEW_IN_DRAWER.value?.title || '';
  inlineNote.notes = '';
};

const formatNoteDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return ''; }
};

const saveInlineNote = async () => {
  if (!inlineNote.title?.trim() || !inlineNote.notes?.trim()) {
    window.$message.warning('Please fill in both title and note fields.');
    return;
  }
  const profile = PROFILE_TO_VIEW_IN_DRAWER.value;
  if (!profile?.uuid) return;

  // Derive profileType from the drawer profile type
  const profileTypeMap = {
    'ASSET_PROFILE': 'ASSET',
    'COMPANY_PROFILE': 'COMPANY',
    'COUNTRY_PROFILE': 'COUNTRY',
  };
  const profileType = profileTypeMap[profile.type] ?? 'ASSET';

  noteSaving.value = true;
  try {
    mountNote({
      id: null,
      title: inlineNote.title,
      element: LAST_SELECTED_ELEMENT_TO_VIEW.value?.[0] ?? null,
      profileUUID: profile.uuid,
      profileType,
      taggedPersons: [],
      note: inlineNote.notes,
      type: 'PERSONAL',
      subscriptionPackageId: null,
    });
    await updateOrCreateNote();
    unMountNote();
    clearInlineNote();
    showAddNoteForm.value = false;
    await getUserNotes();
    window.$message.success('Note saved.');
  } catch (e) {
    console.error(e);
    window.$message.error('Failed to save note.');
  } finally {
    noteSaving.value = false;
  }
};

const handleDeleteDrawerNote = async (noteId) => {
  if (!confirm('Are you sure you want to delete this note?')) return;
  await _deleteNote(noteId);
  await getUserNotes();
};

// ── Accordion state ────────────────────────────────────────────────────────
const markerDrawerSections = ref({
  location:   false,
  ownership:  false,
  elements:   false,
  profile:    false,
  news:       false,
  project:    false,
  overview:   false,
  notes:      false,
});

const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const currentYear = new Date().getFullYear();

const toggleMarkerSection = (section) => {
  markerDrawerSections.value[section] = !markerDrawerSections.value[section];
};

const PIE_COLORS = ['#213ce5', '#00AEEF', '#88d40f', '#F0C302', '#f25c05'];
const ownershipPieSlices = computed(() => {
  const owners = normalizedOwners.value ?? [];
  if (!owners.length) return [];
  const r = 24;
  if (owners.length === 1) {
    return [{
      d:    `M0,${-r} A${r},${r} 0 1,1 0,${r} A${r},${r} 0 1,1 0,${-r} Z`,
      fill: PIE_COLORS[0],
    }];
  }
  const total = owners.reduce((s, o) => s + (parseFloat(o.percentage) || 0), 0) || 100;
  let angle = -Math.PI / 2;
  return owners.map((o, i) => {
    const pct   = (parseFloat(o.percentage) || 0) / total;
    const sweep = pct * 2 * Math.PI;
    const x1    = Math.cos(angle) * r;
    const y1    = Math.sin(angle) * r;
    angle      += sweep;
    const x2    = Math.cos(angle) * r;
    const y2    = Math.sin(angle) * r;
    const large = sweep > Math.PI ? 1 : 0;
    return {
      d:    `M0,0 L${x1.toFixed(3)},${y1.toFixed(3)} A${r},${r} 0 ${large},1 ${x2.toFixed(3)},${y2.toFixed(3)} Z`,
      fill: PIE_COLORS[i % PIE_COLORS.length],
    };
  });
});
const normalizedOwners = computed(() => {
  const owners = PROFILE_TO_VIEW_IN_DRAWER.value?.owners ?? [];
  if (!owners.length) return [];

  // Single owner with missing/undefined percentage -> assume 100%
  if (owners.length === 1) {
    const o = owners[0];
    return [{ ...o, percentage: o.percentage != null ? o.percentage : 100 }];
  }

  // Multiple owners: if exactly one is missing a percentage,
  // fill it with the remainder so the total sums to 100
  const missing = owners.filter(o => o.percentage == null);
  if (missing.length === 1) {
    const known = owners
      .filter(o => o.percentage != null)
      .reduce((sum, o) => sum + parseFloat(o.percentage), 0);
    const remainder = Math.max(0, 100 - known);
    return owners.map(o =>
      o.percentage == null ? { ...o, percentage: remainder } : o
    );
  }

  return owners;
});
const props = defineProps({
  embedded: { type: Boolean, default: false },
  embeddedFilters: { type: Object, default: null },
});

const localProfiles = ref(null);
const embeddedInProgress = ref(false);

const embeddedSelectedElementLabel = computed(() =>
  SUBSCRIPTION_ELEMENTS_OPTS.value.find(x => x.value === props.embeddedFilters?.element)?.label ?? ''
);

const activeProfiles = computed(() =>
    props.embedded ? localProfiles.value : PUBLISHED_PROFILES.value
);

const mapId = computed(() =>
    props.embedded ? 'interactiveMapProxima-embedded' : 'interactiveMapProxima-fullview'
);

const fetchEmbeddedProfiles = async () => {
  if (!props.embeddedFilters?.element) return;
  await getPublishedProfiles(
      {
        element: [props.embeddedFilters.element],
        assets: null, countries: null, companies: null,
        types: null, status: null, stages: null,
        productGroups: null, products: null, subscriptionPackages: null,
      },
      (result) => { localProfiles.value = result; }
  );
};

const embeddedMapReady = ref(false);
const sortState = ref({ columnKey: "country", order: "ascend" });
const assetsPlotted = ref(-1);
const colors = ref([]);
const isProject = ref(false);
const isOperating = ref(false);
const isClosed = ref(false);
const isMine = ref(false);
const isRefinery = ref(false);
const hasInstallations = ref(false);
const isInstallation = ref(false);
const mapStyle = ref(null);
let filterByStatus = ref(false);
let filterByType = ref(false);
const statusFilters = ref("ALL");
const typeFilters = ref([]);
let legendMenu = ref();
const selectedLegendOpts = ref("ALL");
const activeTab = ref(1);
const sidebarCollapsed = ref(false);

const MAP_OPTS = computed(() => [
  { label: "Equi-Rectangular", key: "equirectangular" },
  { label: "Mercator", key: "mercator" },
  { label: "Globe", key: "globe" },
  { label: "Equal Earth", key: "equalEarth" },
].map(opt => ({
  ...opt,
  props: opt.key === currentMapProjection.value
      ? { style: "color: #2184ff !important; font-weight: 500 !important;" }
      : {},
})));

const MAP_STYLE_OPTS = computed(() => [
  { label: "Street View", key: "Street_View" },
  { label: "Outdoor", key: "Outdoor" },
  { label: "Light", key: "Light" },
  { label: "Dark", key: "Dark" },
  { label: "Satellite", key: "Satellite" },
  { label: "Satellite Street View", key: "Satellite_And_Street_View" },
].map(opt => ({
  ...opt,
  props: opt.key === currentMapStyle.value
      ? { style: "color: #2184ff !important; font-weight: 500 !important;" }
      : {},
})));

const LEGEND_OPTS = [
  { label: "All", key: "ALL" },
  { label: "Type", key: "TYPE" },
  { label: "Status", key: "STATUS" },
];

const openModal = () => { showConfirmationModal.value = true; };
const closeModal = () => { showConfirmationModal.value = false; };

const resetForm = () => {
  isProject.value = false; isOperating.value = false; isClosed.value = false;
  isMine.value = false; isRefinery.value = false; isInstallation.value = false;
  submitForm(false);
};

const submitForm = (retainColors) => {
  let _statusFilters = [];
  let _typeFilters = [];
  if (isMine.value) _typeFilters.push("Mine");
  if (isRefinery.value) _typeFilters.push("Refinery");
  if (isInstallation.value) _typeFilters.push("Installation");
  typeFilters.value = _typeFilters;
  if (isOperating.value) _statusFilters.push("Operating");
  if (isClosed.value) { _statusFilters.push("Closed"); _statusFilters.push("Idle"); _statusFilters.push("Unknown"); }
  if (isProject.value) _statusFilters.push("Project");
  statusFilters.value = _statusFilters;
  filterByStatus.value = _statusFilters.length > 0;
  filterByType.value = _typeFilters.length > 0;
  if (!retainColors) { colors.value = []; }
  else if (colors.value.length > 0) { isProject.value = false; isOperating.value = false; isClosed.value = false; }
  if (retainColors) {
    LAST_SELECTED_ELEMENT_TO_VIEW?.value?.forEach(element => {
      let found = colors.value.find(x => x.code == element);
      if (!found) {
        let name = SUBSCRIPTION_ELEMENTS_OPTS.value.find(x => x.value == element)?.label ?? element;
        let _mapStyle = getMapStyle().replace(".html", "");
        let newColor = _mapStyle == "Light" || _mapStyle == "Outdoor" || _mapStyle == "Street_View" ? "#00213b" : '#FFFFFF';
        colors.value.push({ code: element, color: newColor, name: name });
      }
    });
  }
  updateMapMarkers();
  showConfirmationModal.value = false;
};

const getColor = (element) => {
  const found = colors.value.find(c => c.code === element);
  let _mapStyle = getMapStyle().replace(".html", "");
  return found ? found.color : _mapStyle == "Light" || _mapStyle == "Outdoor" || _mapStyle == "Street_View" ? "#00213b" : '#FFFFFF';
};

const updateColor = (element, newColor) => {
  let name = SUBSCRIPTION_ELEMENTS_OPTS.value.find(x => x.value == element)?.label ?? element;
  let found = colors.value.find(c => c.code === element);
  if (found) { found.color = newColor; } else { colors.value.push({ code: element, color: newColor, name: name }); }
};

const toggleMenu = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  const map = document.getElementById(mapId.value);
  if (!map) return;
  if (sidebarCollapsed.value) {
    map.contentWindow.postMessage("Show_Legend", "*");
  } else {
    map.contentWindow.postMessage("Hide_Legend", "*");
  }
};

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const mapView = ref(false);
const tableView = ref("PROFILES");
const showSupplyChainAgreementInfo = ref(false);
const showMarkerInfo = ref(false);
const PROFILE_TO_VIEW_IN_DRAWER = ref();

// Notes filtered by the currently open drawer profile uuid
const drawerProfileNotes = computed(() => {
  if (!PROFILE_TO_VIEW_IN_DRAWER.value?.uuid) return [];
  return (USER_NOTES.value?.data || []).filter(
      n => n.profileUuid === PROFILE_TO_VIEW_IN_DRAWER.value.uuid
  );
});

// Auto-populate title when drawer opens a new profile
watch(PROFILE_TO_VIEW_IN_DRAWER, (profile) => {
  showAddNoteForm.value = false;
  if (profile?.title) {
    inlineNote.title = profile.title;
    inlineNote.notes = '';
  } else {
    clearInlineNote();
  }
});
const grayScale = ref(true);
const COMMODITY_ID = ref(null);
const PROFILE_TO_VIEW_DETAILS = ref(null);
const COMMODITY_OVERVIEW_DETAILS = ref(null);
let byProductCommodities = ref([]);
const agreementTypesFilter = ref([]);
const companyFilter = ref([]);
const productFilter = ref([]);
const companyProfileData = ref(null);
const countryProfileData = ref(null);
const showMap = ref(true);
const showFilters = ref(false);
const userMapColors = ref({ mine: "", factory: "", closed: "", project: "", operating: "" });
const COLUMN_WIDTHS = { country: 140, company: 160, asset: 180, status: 110, type: 110, productNames: 1200 };
const scrollX = computed(() => Object.values(COLUMN_WIDTHS).reduce((a, b) => a + b, 0));
const productNamesModal = ref({ show: false, content: '' });
const embeddedSelectedElement = ref([]);

const columns = [
  {
    key: "country", width: COLUMN_WIDTHS.country, minWidth: COLUMN_WIDTHS.country,
    defaultSortOrder: "ascend", sorter: true,
    title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.countryHeader") }); },
    render: (row) => h(TableCell, { text: row.country, hasProfile: row.hasCountryProfile, profile: row.fullCountryProfile, onMountProfile: mountCountryProfile }),
  },
  {
    key: "company", width: COLUMN_WIDTHS.company, minWidth: COLUMN_WIDTHS.company, sorter: true,
    title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.companyHeader") }); },
    render: (row) => h(TableCell, { text: row.company }),
  },
  // keep old implementation - remove click functionality for now
  // {
  //   key: "company", width: COLUMN_WIDTHS.company, minWidth: COLUMN_WIDTHS.company, sorter: true,
  //   title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.companyHeader") }); },
  //   render: (row) => h(TableCell, { text: row.company, hasProfile: row.hasCompanyProfile, profile: row.fullCompanyProfile, onMountProfile: mountCompanyProfile }),
  // },
  {
    key: "asset", width: COLUMN_WIDTHS.asset, minWidth: COLUMN_WIDTHS.asset, sorter: true,
    title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.assetHeader") }); },
    render: (row) => h(TableCell, { text: row.asset, hasProfile: true, profile: row.fullAssetProfile, onMountProfile: mountAssetProfile }),
  },
  {
    key: "status", width: COLUMN_WIDTHS.status, minWidth: COLUMN_WIDTHS.status,
    title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.statusHeader") }); },
    render: (row) => {
      const colorMap = { Operating: '#00AEEF', Project: '#F0C302', Closed: '#f25c05', Idle: '#f25c05', Unknown: '#504A4B' };
      const labelMap = { Closed: 'Closed/C&M', Idle: 'Closed/C&M' };
      const color = colorMap[row.status] || '#504A4B';
      const label = labelMap[row.status] || row.status || '—';
      return h('div', { style: 'display:flex;align-items:center;gap:6px;' }, [
        h('div', { style: `width:18px;height:18px;border:1.5px solid ${color};border-radius:3px;display:flex;align-items:center;justify-content:center;flex-shrink:0;` }, [
          h('span', { style: `font-size:11px;color:${color};line-height:1;` }, '✔'),
        ]),
        h('span', { style: 'font-size:12px;font-family:GilroyMedium,sans-serif;color:#000;' }, label),
      ]);
    },
  },
  {
    key: "type", width: COLUMN_WIDTHS.type, minWidth: COLUMN_WIDTHS.type, sorter: true,
    title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.typeHeader") }); },
    render: (row) => {
      const iconBase = 'https://driqq8crwypb5.cloudfront.net/Public_Website_Images/images/icons/';
      const iconMap = {
        'beneficiation plant': 'Consumer.png',
        'factory':             'Factory.png',
        'mine':                'Mine.png',
        'recycler':            'Sustainability.png',
        'refinery':            'Refinery.png',
        'tailings/slag':       'Tailings.png',
      };
      const iconFile = iconMap[(row.type || '').toLowerCase()] || 'Mine.png';
      return h('div', { style: 'display:flex;align-items:center;gap:6px;' }, [
        h('img', {
          src: iconBase + iconFile,
          alt: row.type,
          style: `width:${iconFile === 'Mine.png' ? '19px' : '18px'};height:${iconFile === 'Mine.png' ? '19px' : '18px'};object-fit:contain;flex-shrink:0;`,
          onError: (e) => { e.target.style.display = 'none'; },
        }),
        h('span', { style: 'font-size:12px;font-family:GilroyMedium,sans-serif;color:#000;' }, row.type || '—'),
      ]);
    },
  },
  {
    key: "productNames", width: COLUMN_WIDTHS.productNames, minWidth: COLUMN_WIDTHS.productNames, sorter: true,
    title(column) { return h(TableHeader, { text: t("ProximaList.profilesTable.productsHeader") }); },
    render: (row) => {
      if (!row.productNames) return null;
      return h('span', {
        style: 'display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:pointer; max-width:100%;',
        onClick: () => { productNamesModal.value = { show: true, content: row.productList.join("\n") }; }
      }, row.productNames);
    },
  },
];

const data = computed(() => {
  const countryMap = {};
  for (const nth of PUBLISHED_PROFILES.value?.countryProfiles || []) { countryMap[nth.countryCode] = nth; }
  const companyMap = {};
  const associatedCompanyMap = {};
  for (const nth of PUBLISHED_PROFILES.value?.companyProfiles || []) {
    companyMap[nth.companyId] = nth;
    for (const ith of nth.associatedCompanies || []) { associatedCompanyMap[ith] = nth; }
  }
  hasInstallations.value = PUBLISHED_PROFILES?.value?.assetProfiles?.filter(x => x.type == "VFB").length > 0;
  return (PUBLISHED_PROFILES.value?.assetProfiles || []).map((profile) => {
    const hasCompanyProfile = profile.companyId in companyMap || profile.companyId in associatedCompanyMap;
    const companyProfile = companyMap?.[profile.companyId] || associatedCompanyMap?.[profile.companyId];
    const countryProfile = countryMap?.[profile.countryCode];
    const productList = sortBy(profile.products || [], "productName").map(formatProducts).filter(Boolean);
    return {
      uuid: profile.uuid, hasCountryProfile: profile.countryCode in countryMap,
      countryProfile: countryProfile?.uuid, country: profile.countryName,
      hasCompanyProfile, companyProfile: companyProfile?.uuid, company: profile.companyName,
      significance: profile.significance, asset: profile.short_Name, type: profile.type,
      status: profile.status, products: profile.products, fullAssetProfile: profile,
      fullCompanyProfile: companyProfile, fullCountryProfile: countryProfile, productList, productNames: productList.join(", "),
    };
  });
});

const mountedSupplyChainAgreement = ref(null);
const supplyChainAgreementsColumns = [
  { key: "dateAnnounced", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.dateAnnouncedHeader") }); }, render: (row) => row.dateAnnounced },
  { key: "agreementType", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.agreementTypeHeader") }); }, render: (row) => row.agreementType },
  { key: "supplyCompany", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.supplyCompanyHeader") }); }, render: (row) => row.supplyCompany },
  { key: "partnerCompany", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.partnerCompanyHeader") }); }, render: (row) => row.partnerCompany },
  { key: "products", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.productsHeader") }); }, render: (row) => row.products },
  { key: "volumes", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.volumesHeader") }); }, render: (row) => row.volumes },
  { key: "startDate", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.startDateHeader") }); }, render: (row) => row.startDate },
  { key: "endDate", sorter: true, title(c) { return h(TableHeader, { text: t("ProximaList.supplyChainAgreementsTable.endDateHeader") }); }, render: (row) => row.endDate },
];

const mappedSupplyChainAgreementsData = computed(() => {
  return (PUBLISHED_PROFILES.value?.dealAndContractProfiles || []).map((profile) => ({
    uuid: profile.uuid, dateAnnounced: profile.announcedDate, agreementType: profile.type,
    supplyCompany: (profile.suppliersAndReceivers || []).filter(n => n.partyType === "SUPPLIER").map(n => n.companyName).join(", "),
    partnerCompany: (profile.suppliersAndReceivers || []).filter(n => n.partyType === "RECEIVER").map(n => n.companyName).join(", "),
    products: profile.product,
    volumes: profile.unit && profile.capacity ? `${profile.capacity} ${profile.unit}` : "",
    startDate: profile.announcedStartDate || profile.actualStartDate, endDate: profile.endDate, full: profile,
  }));
});

const supplyChainAgreementsData = computed(() => {
  if (agreementTypesFilter.value.length || companyFilter.value.length || productFilter.value.length) {
    return mappedSupplyChainAgreementsData.value.filter(r =>
        (agreementTypesFilter.value.length === 0 || agreementTypesFilter.value.includes(r.agreementType)) &&
        (companyFilter.value.length === 0 || companyFilter.value.includes(r.supplyCompany) || companyFilter.value.includes(r.partnerCompany)) &&
        (productFilter.value.length === 0 || productFilter.value.includes(r.products))
    );
  }
  return mappedSupplyChainAgreementsData.value;
});

const suppliers = computed(() => (mountedSupplyChainAgreement.value?.full?.suppliersAndReceivers || []).filter(n => n.partyType === "SUPPLIER"));
const receivers = computed(() => (mountedSupplyChainAgreement.value?.full?.suppliersAndReceivers || []).filter(n => n.partyType === "RECEIVER"));
const capacity = computed(() =>
    mountedSupplyChainAgreement.value?.full.capacity && mountedSupplyChainAgreement.value?.full.unit
        ? `${mountedSupplyChainAgreement.value?.full.capacity} ${mountedSupplyChainAgreement.value?.full.unit}` : "");

const rowProps = (row) => ({
  style: "cursor:pointer;",
  onClick: () => { showSupplyChainAgreementInfo.value = true; mountedSupplyChainAgreement.value = row; },
});

const mountAssetProfile = (profile) => {
  const assetProfile = profile;
  PROFILE_TO_VIEW_IN_DRAWER.value = {
    type:                'ASSET_PROFILE',
    uuid:                assetProfile.uuid,
    title:               assetProfile.short_Name || assetProfile.name || assetProfile.alias,
    status:              assetProfile.status,
    coordinates:         assetProfile.coordinates,
    owners:              assetProfile.owners ?? [],
    primary_Commodities: assetProfile.primary_Commodities ?? [],
    elementIconUrls:     assetProfile.elementIconUrls ?? {},
    profile:             assetProfile.profile,
    project:             assetProfile.projects,
    products:            assetProfile.products ?? [],
    news:                [],
    newsLoading:         true,
    accuracy:            assetProfile.pin_Accuracy,
    companyId:           assetProfile.companyId ?? assetProfile.operator_Id ?? null,
  };
  markerDrawerSections.value = {
    location: false, ownership: false, elements: false,
    profile: false, production: false, news: false, project: false, overview: false, notes: false,
  };
  showMarkerInfo.value = true;
  getDrawerArticles(PROFILE_TO_VIEW_IN_DRAWER.value, 1);
};

const mountCountryProfile = (profile) => {
  tableView.value = 'COUNTRY_PROFILES';
  if (LAST_SELECTED_ELEMENT_TO_VIEW.value) profile.selectedElement = LAST_SELECTED_ELEMENT_TO_VIEW.value;
  countryProfileData.value = profile;
};
const mountCompanyProfile = (profile) => {
  if (!profile) return;
  PROFILE_TO_VIEW_IN_DRAWER.value = {
    type:               'COMPANY_PROFILE',
    uuid:               profile.uuid,
    title:              profile.companyName || profile.alias,
    overview:           profile.companyOverview,
    profile:            profile.profileOverview,
    website:            profile.website,
    headquarterCountry: profile.headquarterCountryName,
    ownershipType:      profile.ownershipType,
    stockExchanges:     profile.stockExchanges ?? [],
    miningCommodities:  profile.miningCommodities ?? [],
    refiningProducts:   profile.refiningProducts ?? [],
    commodityOverview:  profile.commodityOverview ?? [],
    coordinates:        profile.coordinates,
  };
  markerDrawerSections.value = {
    location: false, ownership: false, elements: false,
    profile: false, production: false, news: false, project: false,
    overview: false, notes: false, commodities: false, exchanges: false,
  };
  showMarkerInfo.value = true;
};

const assetProfiles = computed(() => PUBLISHED_PROFILES.value?.assetProfiles || []);
const countryProfiles = computed(() => MOUNTED_COUNTRY_PROFILES.value?.profiles || []);

const goToList = () => { router.push({ name: ROUTES.PROXIMA_PROFILES.name }); };

const openTab = (id) => { activeTab.value = id; };

const getProfiles = async () => {
  if (LAST_SELECTED_ELEMENT_TO_VIEW.value.length == 0) {
    window.$message.error("Please select at least one item from the asset group before searching");
    return;
  }
  showFilters.value = false;
  if (localStorage.getItem("map_style") == "Supply_View") await getSupplyData();
  await getPublishedProfiles({
    element: LAST_SELECTED_ELEMENT_TO_VIEW.value,
    assets: ASSET_TO_VIEW.value,
    countries: COUNTRY_TO_VIEW.value,
    companies: COMPANY_TO_VIEW.value,
    types: TYPE_TO_VIEW.value,
    status: STATUS_TO_VIEW.value,
    stages: PROFILES_FILTERS.stages,
    productGroups: PRODUCT_GROUP_TO_VIEW.value,
    products: PRODUCT_TO_VIEW.value,
    subscriptionPackages: LAST_SELECTED_PACKAGE_TO_VIEW.value,
  });
};

const resetFilters = async () => {
  ASSET_TO_VIEW.value = null; COMPANY_TO_VIEW.value = null; COUNTRY_TO_VIEW.value = null;
  STATUS_TO_VIEW.value = null; TYPE_TO_VIEW.value = null; PRODUCT_GROUP_TO_VIEW.value = null; PRODUCT_TO_VIEW.value = null;
  setTimeout(() => {
    nextTick(() => {
      const maps = [document.getElementById(mapId.value)];
      for (const map of maps) { if (map) map.src = map.src.split("&")?.[0] + "&_=" + Math.random(); }
    });
  }, 500);
  await getPublishedProfiles({
    element: LAST_SELECTED_ELEMENT_TO_VIEW.value,
    assets: null,
    countries: null,
    companies: null,
    types: null,
    status: null,
    stages: null,
    productGroups: null,
    products: null,
    subscriptionPackages: null,
  });
};

watch(PUBLISHED_PROFILES, () => {
  if (props.embedded) return;
  legendMenu.value = "ALL";
  updateMapMarkers();
});

watch(localProfiles, () => {
  if (!props.embedded) return;
  if (embeddedMapReady.value) {
    updateMapMarkers();
  }
});
watch(embeddedMapReady, (ready) => {
  if (!ready || !props.embedded) return;
  updateMapMarkers();
});

const updateMapType = (projection) => {
  currentMapProjection.value = projection;
  let map = document.getElementById(mapId.value);
  map.contentWindow.postMessage({ reset: false, projection: projection }, "*");
  updateMapMarkers();
};

const updateMapStyle = async (style) => {
  window.$message.info("In order to change the map style, we'll need to reload the map with the new style. Don't worry, we'll save the last style that you use and keep that as your default.", { duration: 10000 });
  localStorage.setItem("map_style", style);
  currentMapStyle.value = style;
  let map = document.getElementById(mapId.value);
  map.src = envs('MAP_BASE') + getMapStyle();
  if (localStorage.getItem("map_style") == "Supply_View") await getSupplyData();
  updateMapMarkers();
};

const getMapStyle = () => {
  let map = (localStorage.getItem("map_style") ?? "Outdoor") + ".html";
  mapStyle.value = MAP_STYLE_OPTS.value.find(x => x.key === (localStorage.getItem("map_style") ?? "Outdoor"))?.label ?? "Outdoor";
  return map;
};

const checkStatus = () => {
  let _mapStyle = getMapStyle().replace(".html", "");
  var defaultColor = _mapStyle == "Light" || _mapStyle == "Outdoor" || _mapStyle == "Street_View" ? "#00213b" : '#FFFFFF';
  if ((isOperating.value || isProject.value || isClosed.value) && colors?.value.filter(x => x.color != defaultColor)?.length > 0) {
    openModal();
  } else {
    submitForm(colors?.value.filter(x => x.color != defaultColor)?.length > 0);
  }
};

const updateMapMarkers = (type_or_status, groups) => {
  const profiles = props.embedded ? localProfiles.value : PUBLISHED_PROFILES.value;
  const selectedElements = props.embedded ? embeddedSelectedElement.value : LAST_SELECTED_ELEMENT_TO_VIEW.value;

  selectedElements.forEach(element => {
    let found = colors.value.find(x => x.code == element);
    if (!found) {
      let name = SUBSCRIPTION_ELEMENTS_OPTS.value.find(x => x.value == element)?.label ?? element;
      let _mapStyle = getMapStyle().replace(".html", "");
      let newColor = _mapStyle == "Light" || _mapStyle == "Outdoor" || _mapStyle == "Street_View" ? "#00213b" : '#FFFFFF';
      colors.value.push({ code: element, color: newColor, name: name });
    }
  });
  try {
    let marks = [];
    if (profiles?.assetProfiles) {
      for (let nth of profiles.assetProfiles || []) {
        let group = null;
        if (type_or_status === "TYPE") { group = nth.type; colors.value = []; filterByType = true; }
        else if (type_or_status === "STATUS") { group = nth.status; colors.value = []; filterByStatus = true; }
        let elements = getElement(nth.primary_Commodities);
        let element = "";
        if (elements?.length > 0) { element = elements[0]; }
        else {
          elements = getElement(nth.by_Product_Commodities);
          if (elements?.length > 0) { element = elements[0]; }
          else {
            elements = getElement(nth.by_Non_Commercial_Commodities);
            if (elements?.length > 0) { element = elements[0]; }
            else {
              elements = getElement(nth.access_Controls);
              element = elements.length > 0 ? elements[0] : nth.primary_Commodities[0];
            }
          }
        }
        marks.push({
          selectedElement: selectedElements, assetCount: profiles?.count,
          type: "Feature",
          properties: {
            type: nth.type,
            country: nth.countryName,
            group: group ?? element,
            element: element,
            status: nth.status,
            uuid: nth.uuid,
            name: nth.name || nth.alias,
            owners: nth.owners,
            products: nth.products,
            primary_Commodities: nth.primary_Commodities,
            companyProfileIds: (profiles?.companyProfiles || []).map(c => c.id),
            elementIconUrls: nth.elementIconUrls ?? {},
          },
          geometry: { type: "Point", coordinates: nth.coordinates?.split(",").map(ith => parseFloat(ith.trim())).reverse() || [] },
        });
      }
    }
    if (type_or_status && groups) { marks = marks.filter(nth => groups?.includes(nth.properties.group)); }
    let elements = [];
    selectedElements.forEach(element => {
      let name = SUBSCRIPTION_ELEMENTS_OPTS.value.find(x => x.value == element)?.label ?? element;
      elements.push(name);
    });
    setTimeout(() => {
      nextTick(() => {
        const maps = [document.getElementById(mapId.value)];
        let _mapStyle = getMapStyle().replace(".html", "");
        var defaultColor = _mapStyle == "Light" || _mapStyle == "Outdoor" || _mapStyle == "Street_View" ? "#00213b" : '#FFFFFF';
        for (const map of maps) {
          if (map) {
            map.contentWindow.postMessage({
              reset: false, marks: JSON.parse(JSON.stringify(marks)),
              colors: JSON.parse(JSON.stringify(colors.value.filter(x => x.color != defaultColor))),
              filterByStatus: filterByStatus.value, filterByType: filterByType.value,
              statusFilters: JSON.parse(JSON.stringify(statusFilters.value)),
              typeFilters: JSON.parse(JSON.stringify(typeFilters.value)),
              allElements: JSON.parse(JSON.stringify(elements)),
              supplyData: JSON.parse(JSON.stringify(SUPPLY_DATA.value))
            }, "*");
          }
        }
      });
    }, 1000);
  } catch (err) { console.log(err); }
};
const getElement = (elements) => elements.filter(x => colors?.value?.some(color => color.code === x));

const formatProductCapacity = (product) => {
  const grossContained = product?.grossContained?.toLowerCase() === "gross weight" ? null : product?.grossContained;
  return [product?.capacity, product?.unit, grossContained].filter(n => n != null && n !== "").join(" ");
};

const formatProducts = (product) =>
    [product?.productName, product?.commission, product?.status, formatProductCapacity(product)].filter(Boolean).join(" - ");

const formatStatusColor = (status) => {
  if (status === "Project") return "#F0C302";
  else if (status === "Operating") return "#00AEEF";
  else if (status === "Idle") return "#f25c05";
  else if (status === "Closed") return "#f25c05";
  else if (status === "Unknown") return "#BFBFBF";
  return "none";
};

const formatAssetProfilePopup = (nth) => {
  const statusColorMap = {
    'Operating': '#00AEEF',
    'Project':   '#F0C302',
    'Idle':      '#f25c05',
    'Closed':    '#f25c05',
    'Unknown':   '#888888',
  };
  const statusColor = statusColorMap[nth.status] || '#888';

  // Element tiles from primary_Commodities e.g. ['Al', 'F']
  const elementTilesHtml = (nth.primary_Commodities || []).map(code => {
    const iconUrl = (nth.elementIconUrls ?? {})[code];
    if (iconUrl) {
      return `
            <div class="el-tile" style="background:transparent;border:1px solid #e5e7eb;">
              <img src="${iconUrl}" alt="${code}"
                   style="width:38px;height:38px;object-fit:contain;" />
            </div>`;
    }
    const el = getElement(code);
    if (el) {
      return `
            <div class="el-tile">
              <div class="el-row-top">
                <span class="el-num">${el.number}</span>
                <span class="el-mass">${parseFloat(el.atomic_mass).toFixed(3)}</span>
              </div>
              <span class="el-sym">${el.symbol}</span>
              <span class="el-name">${el.name}</span>
            </div>`;
    }
    return `<div class="el-tile-text">${code}</div>`;
  }).join('');
  const ownershipHtml = nth.owners?.length
      ? nth.owners.map(kth =>
          `<span class="pp-co-link" style="display:block; color: #000; font-weight: 400">
          ${kth.name || ''} (${kth.percentage || ''}%)
        </span>`
      ).join('')
      : '<span style="font-size:12px;color:#000;font-style:italic;">—</span>';

  const productsHtml = nth.products?.length
      ? nth.products.map(kth =>
          `<div class="pp-value">
          ${kth.productName || ''}${kth.capacity ? ' — ' + kth.capacity : ''}${kth.unit ? ' ' + kth.unit : ''}
          ${kth.status ? `<span style="font-size:10px;">(${kth.status})</span>` : ''}
        </div>`
      ).join('')
      : '<span class="pp-value">—</span>';

  return `
    <div style="font-family:Arial,Helvetica,sans-serif;min-width:300px;max-width:340px;">
      <div class="pp-header">
        <a href="javascript:window.parent.postMessage({ type: 'ASSET_PROFILE', uuid: '${nth.uuid}'}, '*');"
           class="pp-name"
           onmouseover="this.style.color='#2184ff';this.style.textDecoration='underline';this.style.fontWeight='600';"
           onmouseout="this.style.color='#000';this.style.textDecoration='none';this.style.fontWeight='700';">
          ${nth.name || nth.alias}
        </a>
        <span class="pp-status" style="background:${statusColor};">${nth.status || ''}</span>
      </div>
      <div class="pp-elements">${elementTilesHtml}</div>
      <div class="pp-body">
        <div class="pp-section">
          <div class="pp-label">Ownership</div>
          ${ownershipHtml}
        </div>
        <div class="pp-section">
          <div class="pp-label">Markets</div>
          <div class="pp-value">Primary: ${(nth.primary_Commodities || []).join(', ')}</div>
        </div>
        <div class="pp-section">
          <div class="pp-label">Products</div>
          ${productsHtml}
        </div>
      </div>
    </div>`;
};

const goToAssetProfile = (nth) => {
  if (nth?.assetUUID) {
    router.push({ name: ROUTES.ASSET_PROFILE.name, params: { id: nth?.assetUUID }, query: { element: LAST_SELECTED_ELEMENT_TO_VIEW.value, email: userEmail } });
  }
};

const goToCompanyProfile = (companyUUID) => {
  if (companyUUID) {
    router.push({ name: ROUTES.COMPANY_PROFILE.name, params: { id: companyUUID }, query: { element: LAST_SELECTED_ELEMENT_TO_VIEW.value, email: userEmail } });
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const getAuthorUrl = (authorName) => {
  if (!authorName) return '#';
  return `${pbDomain}/about-us?people=${authorName.replaceAll(' ', '_')}`;
};

const viewArticle = (nth) => {
  const baseUrl = `${pbDomain}/blue`;
  const slug    = nth.name?.replaceAll(' ', '-').toLowerCase();
  let url = '';
  switch (nth.type) {
    case 'NEWS':    url = `${baseUrl}/news-analysis/${nth.id}/${slug}`; break;
    case 'OPINION': url = `${baseUrl}/opinion-pieces/${nth.id}/${slug}`; break;
    case 'REPORT':
    case 'INSIGHT': url = `${baseUrl}/reports/${nth.id}/${slug}`; break;
    case 'VIDEO':   url = `${baseUrl}/videos/${nth.id}`; break;
    default:        url = `${baseUrl}/news-analysis/${nth.id}/${slug}`;
  }
  window.open(url, '_blank');
};

const scrollToTable = () => { const el = document.getElementById("info-table"); el.scrollIntoView(); };

window.addEventListener("message", (event) => {
  if (event.data?.type === "ASSET_PROFILE") {
    const profiles = props.embedded ? localProfiles.value : PUBLISHED_PROFILES.value;
    const assetProfile = (profiles?.assetProfiles || [])?.find(n => n.uuid === event.data.uuid);
    if (!assetProfile) return;
    PROFILE_TO_VIEW_IN_DRAWER.value = {
      type:                event.data.type,
      uuid:                assetProfile.uuid,
      title:               assetProfile.name || assetProfile.alias,
      status:              assetProfile.status,
      coordinates:         assetProfile.coordinates,
      owners:              assetProfile.owners ?? [],
      primary_Commodities: assetProfile.primary_Commodities ?? [],
      elementIconUrls:     assetProfile.elementIconUrls ?? {},
      profile:             assetProfile.profile,
      project:             assetProfile.projects,
      products:            assetProfile.products ?? [],
      news:                [],
      newsLoading:         true,
      accuracy:            assetProfile.pin_Accuracy,
      companyId:           assetProfile.companyId ?? assetProfile.operator_Id ?? null,
    };
    markerDrawerSections.value = {
      location: false, ownership: false, elements: false,
      profile: false, production: false, news: false, project: false, overview: false, notes: false,
    };
    showMarkerInfo.value = true;
    getDrawerArticles(PROFILE_TO_VIEW_IN_DRAWER.value, 1);
  } else if (event.data?.type === "COMPANY_PROFILE") {
    const profiles = props.embedded ? localProfiles.value : PUBLISHED_PROFILES.value;
    const companyProfile = (profiles?.companyProfiles || [])?.find(n => event.data.id === n.id);
    if (companyProfile) {
      PROFILE_TO_VIEW_IN_DRAWER.value = {
        type: 'COMPANY_PROFILE',
        uuid: companyProfile.uuid,
        title: companyProfile.companyName || companyProfile.alias,
        overview: companyProfile.companyOverview,
        profile: companyProfile.profileOverview,
        website: companyProfile.website,
        headquarterCountry: companyProfile.headquarterCountryName,
        ownershipType: companyProfile.ownershipType,
        stockExchanges: companyProfile.stockExchanges ?? [],
        miningCommodities: companyProfile.miningCommodities ?? [],
        refiningProducts: companyProfile.refiningProducts ?? [],
        commodityOverview: companyProfile.commodityOverview ?? [],
        coordinates: companyProfile.coordinates,
    };
    markerDrawerSections.value = {
      location: false, ownership: false, elements: false,
      profile: false, production: false, news: false, project: false,
      overview: false, notes: false, commodities: false, exchanges: false,
      };
      showMarkerInfo.value = true;
    }
  } else if (event.data === "FRAME_LOADED") {
    if (props.embedded) embeddedMapReady.value = true;
    updateMapMarkers();
  } else if (typeof event.data === "number") {
    assetsPlotted.value = event.data;
  }
});

const companyHasProfile = (id) => {
  const profiles = props.embedded ? localProfiles.value : PUBLISHED_PROFILES.value;
  return !!(profiles?.companyProfiles || [])?.find(n => id === n.id);
};

const cleanup = () => {
  if (!showMarkerInfo.value) PROFILE_TO_VIEW_IN_DRAWER.value = null;
  PROFILE_ID.value = null; COMMODITY_ID.value = null; COUNTRY_ID.value = null; PROFILE_TO_VIEW_DETAILS.value = null;
  if (route.query.profileType) {
    router.push({ path: route.path, query: { _: Math.random() } });
    COMMODITY_ID.value = null; COMMODITY_OVERVIEW_DETAILS.value = null;
  }
};

const saveToPNG = () => {
  // Track graphic / data export
  if (showMap.value == true) {
    logUserAction(
      USER_ACTIONS.DOWNLOAD_PROFILES_GRAPH,
      `Exported Asset Profiles Map Graphic`
    );
  } else {
    logUserAction(
      USER_ACTIONS.DOWNLOAD_PROFILES_DATA,
      `Downloaded Asset Profiles Data Table (Excel: ${data.value.length} assets)`
    );
  }

  setTimeout(() => {
    nextTick(() => {
      if (showMap.value == true) {
        const maps = [document.getElementById("interactiveMapProxima")];
        for (const map of maps) { if (map) { map.contentWindow.postMessage("DOWNLOAD_MAP", "*"); map.contentWindow.console = console; } }
      } else {
        if (data.value.length == 0) { window.$message.console.warn("No data to download"); return; }
        let formattedData = data.value.map(asset => ({
          "Asset Name": asset.asset, "Country Name": asset.country, "Company Name": asset.company,
          "Significance": asset.significance ?? "N/A", "Status": asset.status, "Type": asset.type,
          "Products": asset.productList.join("; ")
        }));
        let worksheet = XLSX.utils.json_to_sheet(formattedData);
        let workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Proxima Data");
        XLSX.writeFile(workbook, "Proxima Data Extract.xlsx");
      }
    });
  }, 1000);
};

// Track view mode switch (Map vs Table)
watch(showMap, (isMap) => {
  logUserAction(
    USER_ACTIONS.VIEW_PROFILES,
    `Switched Asset Profiles view to: "${isMap ? 'Map' : 'Table'}"`
  );
});

const showCompanyProfile = (data) => { tableView.value = 'COMPANY_PROFILES'; companyProfileData.value = data; };

const agreementTypeOpts = computed(() => { let opts = mappedSupplyChainAgreementsData.value.map(r => ({ label: r.agreementType, value: r.agreementType })); return uniqBy(sortBy(opts, "label"), "label"); });
const companyOpts = computed(() => { let all = [...mappedSupplyChainAgreementsData.value.filter(r => r.supplyCompany != '').map(r => ({ label: r.supplyCompany, value: r.supplyCompany })), ...mappedSupplyChainAgreementsData.value.filter(r => r.partnerCompany != '').map(r => ({ label: r.partnerCompany, value: r.partnerCompany }))]; return uniqBy(sortBy(all, "label"), "label"); });
const productOpts = computed(() => { let opts = mappedSupplyChainAgreementsData.value.map(r => ({ label: r.products, value: r.products })); return uniqBy(sortBy(opts, "label"), "label"); });

watch(SUBSCRIPTION_ELEMENTS_OPTS, () => {
  if (LAST_SELECTED_ELEMENT_TO_VIEW.value) return;
  LAST_SELECTED_ELEMENT_TO_VIEW.value = SUBSCRIPTION_ELEMENTS_OPTS?.value[0]?.value ? [SUBSCRIPTION_ELEMENTS_OPTS?.value[0]?.value] : [];
});

watch(() => tableView === 'SUPPLY_CHAIN_AGREEMENTS' ? supplyChainAgreementsData?.value : data?.value, (newValue) => { tableData.value = [...newValue]; }, { immediate: true });

const handleSort = (sorter) => {
  if (sorter.order === false) sorter.order = "descend";
  if (sorter.order === "ascend" || sorter.order === "descend") sortState.value = sorter;
};

watch(sortState, (newValue) => { if (newValue.order === false) sortState.value.order = "ascend"; });

const sortedData = computed(() => {
  let _data = tableView === 'SUPPLY_CHAIN_AGREEMENTS' ? supplyChainAgreementsData?.value : data?.value;
  if (!sortState.value || !sortState.value.columnKey) return _data;
  return [..._data].sort((a, b) => {
    let aValue = a[sortState.value.columnKey];
    let bValue = b[sortState.value.columnKey];
    if (Array.isArray(aValue)) aValue = aValue[0];
    if (Array.isArray(bValue)) bValue = bValue[0];
    if (sortState.value?.columnKey === "updatedAt") {
      const aDate = aValue ? new Date(aValue) : 0;
      const bDate = bValue ? new Date(bValue) : 0;
      if (aDate === bDate) return 0;
      return sortState.value.order === "ascend" ? aDate - bDate : bDate - aDate;
    }
    if (aValue === bValue) return 0;
    return sortState.value.order === "ascend" ? (aValue < bValue ? -1 : 1) : (aValue > bValue ? -1 : 1);
  });
});

onMounted(async () => {
  // Track Asset Profiles landing access
  logUserAction(
    USER_ACTIONS.VIEW_PROFILES,
    `Viewed Asset Profiles directory`
  );

  await getSubscriptionInfo(false);
  await getUserNotes();
  if (props.embedded) {
    if (!SUBSCRIPTION_ELEMENTS_OPTS?.value?.length) {
      await new Promise((resolve) => {
        const stop = watch(SUBSCRIPTION_ELEMENTS_OPTS, (val) => { if (val?.length) { stop(); resolve(); } }, { immediate: false });
        setTimeout(() => { stop(); resolve(); }, 5000);
      });
    }
    const element = props.embeddedFilters?.element ?? SUBSCRIPTION_ELEMENTS_OPTS?.value?.[0]?.value;
    if (element) {
      embeddedSelectedElement.value = [element];
    } else {
      console.warn('[ProximaProfiles embedded] No subscription elements available — map will load without markers.');
    }
    if (embeddedSelectedElement.value?.length) {
      try { await fetchEmbeddedProfiles(); }
      catch (e) { console.warn('[ProximaProfiles embedded] fetchEmbeddedProfiles error:', e); }
    }
    return;
  }
  if (route.query.element) LAST_SELECTED_ELEMENT_TO_VIEW.value = route.query.element ? [route.query.element] : [];
  if (LAST_SELECTED_ELEMENT_TO_VIEW.value && (PUBLISHED_PROFILES.value?.assetProfiles || []).length == 0) {
    await getPublishedProfiles({
      element: LAST_SELECTED_ELEMENT_TO_VIEW.value,
      assets: ASSET_TO_VIEW.value, countries: COUNTRY_TO_VIEW.value,
      companies: COMPANY_TO_VIEW.value, types: TYPE_TO_VIEW.value,
      status: STATUS_TO_VIEW.value, stages: PROFILES_FILTERS.stages,
      productGroups: PRODUCT_GROUP_TO_VIEW.value, products: PRODUCT_TO_VIEW.value,
      subscriptionPackages: LAST_SELECTED_PACKAGE_TO_VIEW.value,
    });
  }
  if (route.query.profileType) {
    if (route.query.profileType === "COMPANY") tableView.value = "COMPANY_PROFILES";
    else if (route.query.profileType === "COUNTRY") tableView.value = "COUNTRY_PROFILES";
  }
});

watch(showMap, async () => { if (!props.embedded) sessionStorage.setItem("showMap", showMap.value); });
watch(
    () => props.embeddedFilters?.element,
    async (newElement) => {
      if (!props.embedded || !newElement) return;
      embeddedSelectedElement.value = [newElement];
      try { await fetchEmbeddedProfiles(); }
      catch (e) { console.warn(e); }
    }
);

onBeforeMount(() => {
  if (props.embedded) { showMap.value = true; }
  else { showMap.value = (sessionStorage.getItem("showMap") ?? 'true') == 'true' ? true : false; }
  if (!props.embedded) window.$loading.start();
});
</script>

<style lang="scss" scoped>
.drawer-news-list {
  display: flex;
  flex-direction: column;
}

.drawer-news-item {
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background-color: #f8f9fa; }
  &:last-child { border-bottom: none; }
}

.news-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  font-size: 0.6875rem;
  color: #718096;
}
.news-date     { font-weight: 500; }
.meta-separator { opacity: 0.6; }
.news-authors  { display: flex; gap: 0.25rem; }
.author {
  &:not(:first-child)::before { content: "• "; margin-right: 0.25rem; }
}
.author-link {
  color: #718096;
  text-decoration: none;
  transition: color 0.2s;
  &:hover { color: #2184ff; }
}

.drawer-news-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #000;
  margin: 0 0 0.4rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  &:hover { color: #2184ff; }
}

.drawer-news-description {
  font-size: 0.75rem !important;
  color: #000;
  line-height: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  :deep(p)    { margin: 0; font-size: 0.75rem !important; background-color: transparent !important; }
  :deep(span) { font-size: 0.75rem !important; background-color: transparent !important; }
}
.notes-badge {
  background: #213ce5;
  color: white;
  font-size: 0.7rem !important;
  padding: 0.02rem 0.3rem;
  border-radius: 50% !important;
  min-width: 1.25rem;
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.notes-timeline-wrapper {
  padding: 0.5rem 0;
}

.note-preview {
  font-size: 0.8rem;
  color: #718096;
  margin-top: 0.15rem;
  line-height: 1.5;
}

.note-date {
  font-size: 11px;
  color: #718096;
  margin-right: 8px;
}

/* ── Inline note form ── */
.inline-note-form {
  margin-top: 12px;
}

.add-note-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  cursor: pointer;
  user-select: none;
  &:hover .add-note-toggle__label { color: #213ce5; }
}

.add-note-toggle__line {
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

.add-note-toggle__label {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
  i { font-size: 10px; }
}

/* ── Notes accordion content extra padding ── */
.accordion-content--notes {
  padding-bottom: 1rem;
}

/* ── Modal backdrop ── */
.pn-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.pn-modal {
  background: white;
  width: 520px;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  overflow: hidden;
}
.pn-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  font-family: "GilroyBold", sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: #00213b;
}
.pn-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #718096;
  padding: 0;
  line-height: 1;
  &:hover { color: #000; }
}
.pn-modal-body {
  padding: 1rem;
  font-size: 0.8125rem;
  font-family: "GilroyMedium", sans-serif;
  color: #000;
  line-height: 1.6;
  overflow-y: auto;
  white-space: pre-wrap;
  scrollbar-width: thin;
}

/* ── Marker drawer ── */
.marker-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: "GilroyMedium", sans-serif;
}

.drawer-content {
  flex: 1;
  overflow-x: hidden;
  padding-bottom: 0.5rem;
  min-height: 0;
}

.marker-drawer-header {
  padding: 0.5rem;
  border-bottom: 1px solid #e9ecef;
  background: white;
}

.marker-drawer-header__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.marker-drawer-header__name {
  font-family: "GilroyBold", sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: #000;
  flex: 1;
  min-width: 0;
}

.marker-drawer-header__badge {
  flex-shrink: 0;
  font-family: "GilroyBold", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  padding: 3px 10px;
  white-space: nowrap;
  line-height: 1.4;
  border-radius: 3px;
}

/* ── Accordion ── */
.accordion-section {
  border-bottom: 1px solid #e9ecef;
}
.accordion-section:last-child {
  border-bottom: none;
}

.accordion-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}
.accordion-header:hover { background: #f8f9fa; }

.header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
}
.header-left span {
  font-family: "GilroyBold", sans-serif !important;
  font-size: 0.875rem;
}
.header-left i {
  color: #213ce5;
  font-size: 0.875rem;
  width: 20px;
  text-align: center;
}
.section-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.logout-btn .section-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.accordion-header .fa-chevron-down {
  color: #718096;
  font-size: 0.85rem;
  transition: transform 0.3s ease;
}
.accordion-header .fa-chevron-down.rotated {
  transform: rotate(180deg);
}

.accordion-content {
  padding: 0 1.25rem 1rem 1.25rem;
  background: white;
}
.accordion-content--flush {
  padding: 0 0.5rem;
}

.marker-drawer-body-text {
  margin: 0;
  font-family: "GilroyMedium", sans-serif;
  font-size: 0.8125rem;
  color: #000;
  line-height: 1.6;
  white-space: pre-line;
}

.marker-drawer-coords {
  margin: 0;
  font-size: 0.8125rem !important;
  color: #000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.marker-drawer-coords__pin { color: #f25c05; }

.marker-drawer-ownership {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.marker-drawer-ownership__list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.marker-drawer-ownership__pie { flex-shrink: 0; }

.marker-drawer-owner {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.8125rem;
}
.marker-drawer-owner__pct {
  font-family: "GilroyMedium", sans-serif;
  font-size: 0.8125rem;
  width: 3rem;
  min-width: 3rem;
  flex-shrink: 0;
  color: #000;
  font-variant-numeric: tabular-nums;
  text-align: left;
  padding-right: 0.5rem;
}
.marker-drawer-owner__name { color: #000; }

.marker-drawer-elements {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.marker-drawer-el-tile {
  width: 38px;
  height: 38px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.marker-drawer-el-tile--text {
  background: #213ce5;
  color: #fff;
  font-family: "GilroyMedium", sans-serif;
  font-size: 13px;
  font-weight: 700;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  :deep(.n-data-table) { height: 100%; }

  :deep(.n-data-table-base-table-body) {
    scrollbar-width: initial;
    scrollbar-color: auto;
    &::-webkit-scrollbar { width: 6px; height: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 99px; transition: background 0.2s ease; }
    &::-webkit-scrollbar-thumb:hover { background: #aaa; }
    &::-webkit-scrollbar-button { display: none !important; height: 0 !important; width: 0 !important; }
  }
}

.marker-drawer-table-wrap {
  overflow-x: auto;
}
.marker-drawer-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  font-family: "GilroyMedium", sans-serif;
}
.marker-drawer-table thead tr {
  background: #213ce5;
  color: #fff;
}
.marker-drawer-table thead th {
  padding: 5px 10px;
  font-family: "GilroyBold", sans-serif;
  font-size: 0.75rem;
  text-align: center;
  border: 1px solid white;
  white-space: nowrap;
}
.marker-drawer-table__empty-th {
  background: transparent !important;
}
.marker-drawer-table tbody tr:nth-child(even) { background: #f2f2f2; }
.marker-drawer-table tbody td {
  padding: 5px 10px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;
  color: #000;
  font-size: 0.8125rem;
}
.marker-drawer-table__label {
  text-align: left !important;
  color: #000;
  font-family: "GilroyMedium", sans-serif;
  white-space: nowrap;
}

.marker-drawer-news {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.marker-drawer-news-item {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 0.8125rem;
  line-height: 1.4;
}
.marker-drawer-news-item__dot {
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  background: #213ce5;
  margin-top: 5px;
}
.marker-drawer-news-item__link {
  color: #213ce5;
  text-decoration: none;
  font-family: "GilroyMedium", sans-serif;
}
.marker-drawer-news-item__link:hover { text-decoration: underline; }
.marker-drawer-news-item__text {
  color: #000;
  font-family: "GilroyMedium", sans-serif;
}

.empty-state {
  padding: 1rem 0;
  text-align: center;
}
.empty-state p {
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
}

/* ── Sticky footer ── */
.drawer-logout {
  flex-shrink: 0;
  border-top: 1px solid #e9ecef;
  background: white;
}
.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem;
  color: #00213b;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: "GilroyBold", sans-serif;
  transition: background 0.2s ease, color 0.2s ease;
  cursor: pointer;
  border: none;
  background: transparent;
}
.logout-btn:hover { background: #f2f2f2; }
.logout-btn i {
  font-size: 0.875rem;
  width: 20px;
  text-align: center;
  color: #213ce5;
}

/* ── Embedded mode ── */
.embedded-map-container { width: 100%; height: 100%; position: relative; }
.embedded-iframe-wrapper { width: 100%; height: 100%; position: relative; overflow: hidden; }
.embedded-filter-pill {
  position: absolute;
  top: 0.25rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: fit-content;
  padding: 0 0.5rem;
  background: #f8f9fa;
  color: #000;
  font-size: 15.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}
.embedded-loading-overlay {
  width: 100%; height: 100%;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  background-color: #f8f9fa;
}

/* ── Page wrapper ── */
.proxima-wrap {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px);
  overflow: hidden;
  padding: 0;
}

/* ── Section header card ── */
.section-header-card {
  border: none;
  border-radius: 0;
  background: #fff;

  .header-title {
    font-size: 1rem;
    font-weight: 700;
    color: #000;
  }
}
.card {
  box-shadow: none;
}

.page-actions {
  display: flex;
  gap: 0 1rem;
  padding: 0;
  flex-wrap: wrap;
}
.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.365rem;
  padding: 0.5rem 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  font-size: 14px;
  color: #000;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { color: #2184ff; text-decoration: underline; font-weight: 600; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.notes-header-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: auto;
  padding: 0.5rem 1rem;
  font-size: 13px;

  .section-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
    border-radius: 50%;
  }

  .notes-badge {
    background: rgba(255, 255, 255, 0.25);
  }
}

/* ── Main two-column layout ── */
.profiles-layout {
  display: flex;
  flex: 1;
  gap: 0.5rem;
  min-height: 0;
  overflow: hidden;
}

/* ── Sidebar ── */
.sidebar {
  margin-bottom: 0.5rem;
  width: 200px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #f2f2f2;
  position: relative;
  transition: width 0.2s ease;
  overflow: hidden;

  &--collapsed {
    width: 22px;
  }
}

.collapse-btn {
  width: 15px;
  height: 48px;
  background: #213ce5;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: absolute;
  top: 0.5rem;
  right: 0;

  .sidebar--collapsed & {
    position: relative;
    top: 0.5rem;
    right: unset;
    align-self: center;
  }
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  padding: 0.5rem;
}

/* ── Sidebar toggle ── */
.tab-toggle {
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 3px;
  border-radius: 3px;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
  background: linear-gradient(90deg, #213ce5, #2380f2);
  cursor: pointer;
  user-select: none;
}

.tab-toggle__label {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: white;
  pointer-events: none;
  z-index: 1;
}

.tab-toggle__thumb {
  position: absolute;
  right: 3px;
  top: 3px;
  bottom: 3px;
  width: 24px;
  border-radius: 4px;
  background: white;
  transition: right 0.25s cubic-bezier(0.4, 0, 0.2, 1),
  left  0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.tab-toggle__thumb--left {
  right: unset;
  left: 3px;
}

/* ── Filter panel ── */
.filter-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.filter-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.5rem;

  :deep(.n-base-selection__placeholder),
  :deep(.n-base-selection-placeholder),
  :deep(.n-base-selection-input__placeholder) {
    font-size: 11px !important;
    font-family: 'GilroyMedium', sans-serif !important;
  }
  :deep(.n-base-selection) { min-height: 26px !important; height: auto; }
  :deep(.n-base-selection-input) { font-size: 11px !important; }
  :deep(.n-base-selection__placeholder) { font-size: 11px !important; color: #9ca3af; }
  :deep(.n-base-selection-tag__close) { font-size: 10px; }
  :deep(.n-tag) { font-size: 10px !important; height: 18px !important; padding: 0 0.5rem !important; }
}

.filter-btns {
  display: flex;
  gap: 4px;
  margin-top: 6px;
  flex-shrink: 0;
}

.filter-btn {
  flex: 1;
  height: 26px;
  font-size: 11px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 500;

  &--primary {
    background: linear-gradient(90deg, #213ce5, #2380f2);
    color: white;
    &:hover { border: 1px solid #00aeef; }
  }

  &--secondary {
    background: linear-gradient(90deg, #213ce5, #2380f2);
    color: white;
    &:hover { border: 1px solid #00aeef; }
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* ── Layout panel ── */
.layout-panel {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  font-size: 11px;
  color: #000;
  padding-right: 0.5rem;
}
.layout-label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #000;
  margin: 0 0 3px;
}
.color-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3px;
}
.color-label { font-size: 11px; color: #000; flex: 1; margin-right: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.color-input { width: 30px; height: 20px; border: 1px solid #d1d5db; border-radius: 2px; padding: 0; cursor: pointer; }

/* ── Legend ── */
.legend-box {
  margin-top: 8px;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}
.legend-row {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  margin-bottom: 3px;
  color: #000;
}
.legend-color-swatch { width: 14px; height: 14px; border: 1px solid #d1d5db; flex-shrink: 0; }

/* ── Main content area ── */
.main-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

/* ── Map ── */
.map-wrapper {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

/* ── Loading overlay ── */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  z-index: 10;
  &--table { position: relative; min-height: 160px; background: transparent; }
}

.custom-loader { display: flex; gap: 5px; align-items: center; }
.loader-circle {
  width: 10px; height: 10px;
  background-color: #00213b;
  border-radius: 50%;
  animation: loader-bounce 1.4s ease-in-out infinite both;
  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
  &:nth-child(4) { animation-delay: 0.16s; }
}
@keyframes loader-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
  40%            { transform: scale(1); opacity: 1; }
}
.loading-text { color: #00213b; font-size: 12px; font-weight: 500; margin: 0; }

/* ── Chevrons ── */
.chevron-left {
  display: inline-block; width: 7px; height: 7px;
  border-right: 2px solid white; border-bottom: 2px solid white;
  transform: rotate(-45deg); margin-left: -4px;
}
.chevron-right {
  display: inline-block; width: 7px; height: 7px;
  border-right: 2px solid white; border-bottom: 2px solid white;
  transform: rotate(135deg); margin-right: -3px;
}

/* ── Checkboxes ── */
input[type="checkbox"] { display: none; }
.custom-checkbox {
  position: relative; display: flex; align-items: center;
  font-size: 11px; font-weight: 500; color: #000; margin-bottom: 3px;
  padding-left: 20px; cursor: pointer; gap: 4px;
}
.checkmark {
  width: 14px; height: 14px; border: 1px solid lightgrey; border-radius: 3px;
  display: inline-block; position: absolute; left: 0; top: 1px;
  background-color: #fff; transition: background-color 0.2s ease, border-color 0.2s ease;
}
.PIE_COLORS {
  color: #213ce5;
  color: #00AEEF;
  color:#88d40f;
  color: #F0C302;
  color: #f25c05;
}
input[type="checkbox"]:checked+.checkmark { background-color: #fff; border-color: #F0C302; }
input[type="checkbox"]:checked+.checkmark::after { content: "✓"; position: absolute; left: 2px; top: -2px; font-size: 11px; color: #F0C302; }
input[type="checkbox"]:checked+.operating { background-color: #fff; border-color: #00AEEF; }
input[type="checkbox"]:checked+.operating::after { content: "✓"; position: absolute; left: 2px; top: -2px; font-size: 11px; color: #00AEEF; }
input[type="checkbox"]:checked+.project { background-color: #fff; border-color: #F0C302; }
input[type="checkbox"]:checked+.project::after { content: "✓"; position: absolute; left: 2px; top: -2px; font-size: 11px; color: #F0C302; }
input[type="checkbox"]:checked+.closed { background-color: #fff; border-color: #f25c05; }
input[type="checkbox"]:checked+.closed::after { content: "✓"; position: absolute; left: 2px; top: -2px; font-size: 11px; color: #f25c05; }

.custom-checkbox .checkmark-refinery {
  width: 0; height: 0;
  border-left: 7px solid transparent; border-right: 7px solid transparent; border-bottom: 14px solid #F0C302;
  display: inline-block; position: absolute; left: 0; top: 3px;
  transition: border-bottom-color 0.2s ease-in-out;
  &::before { content: ""; position: absolute; top: 0px; left: -7px; width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent; border-bottom: 14px solid white; }
}
input[type="checkbox"]:checked+.checkmark-refinery { border-bottom-color: #F0C302; }
input[type="checkbox"]:checked+.checkmark-refinery::before { border-bottom-color: #F0C302; }

.custom-checkbox .checkmark-mine {
  width: 14px; height: 14px; border: 1px solid lightgray; background-color: white;
  display: inline-block; position: absolute; left: 0; top: 2px;
  transition: background-color 0.2s ease-in-out;
}
input[type="checkbox"]:checked+.checkmark-mine { background-color: #F0C302; }

.custom-checkbox .checkmark-installation {
  width: 14px; height: 14px; border: 1px solid lightgray; background-color: white;
  display: inline-block; position: absolute; left: 0; top: 2px;
  border-radius: 50%; transition: background-color 0.2s ease-in-out;
}
input[type="checkbox"]:checked+.checkmark-installation { background-color: #F0C302; }

/* ── Modal ── */
.modal {
  display: none; position: fixed; left: 0; top: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4); justify-content: center; align-items: center; z-index: 9999;
}
.modal-content { background: white; padding: 20px; border-radius: 10px; text-align: center; width: 600px; box-shadow: 0 4px 8px rgba(0,0,0,0.2); }
.modal-buttons { margin-top: 15px; display: flex; justify-content: space-around; }
.modal button { width: 30%; padding: 8px; border-radius: 5px; border: none; cursor: pointer; font-size: 13px; }
.confirm-btn, .cancel-btn { background-color: #00213b; color: white; &:hover { background-color: #EBEDEF; color: #F0C302; } }

/* ── Legend shapes ── */
.checkmark-box {
  width: 14px; height: 14px; border: 1px solid; border-radius: 3px;
  display: flex; align-items: center; justify-content: center;
  background-color: white; flex-shrink: 0;
  &::after { content: "✔"; font-size: 11px; }
}
.gold { border-color: #F0C302; &::after { color: #F0C302; } }
.blue { border-color: #00AEEF; &::after { color: #00AEEF; } }
.red  { border-color: #f25c05; &::after { color: #f25c05; } }
.triangle { width: 0; height: 0; border-left: 7px solid transparent; border-right: 7px solid transparent; border-bottom: 14px solid #F0C302; flex-shrink: 0; }
.legend-circle { width: 14px; height: 14px; background-color: #F0C302; border-radius: 50%; flex-shrink: 0; }
.square { width: 14px; height: 14px; background-color: #F0C302; flex-shrink: 0; }

/* ── Scrollbar ── */
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
</style>